// Workspace settings and personal preferences (PHASED-PLAN D2, D2b, F4). Each key has one schema; the server refuses
// unknown keys and values that do not parse. Stored in `workspace_settings` / `person_prefs` (packages/db/sql/16-settings.sql).
import { z } from "zod";
import { HUE, ISO_DATE } from "./common.ts";
import { CLIENT_PORTAL_ACCESS } from "./client-portal-access.ts";
import { QUIET_HOURS } from "./automation.ts";
import { AI_POLICY, AI_REPLY_AGENT } from "./ai.ts";
import { DEFAULT_CONTACT_ROLES, LIFECYCLE } from "./clients.ts";
import { checkFormula } from "./expr.ts";
import { BUDGET_TYPE, STATUS } from "./work.ts";
import { SALES_SETTING } from "./activities.ts";
import { SECURITY, SSO } from "./security.ts";
import { ASSET_CLASS_ID } from "./files.ts";
import { PRODUCT_STATUSES } from "./web-catalogue-x21.ts";
import { DESKTOP_ACTIVITY } from "./devices.ts";
import { CANDIDATE_STAGE_KIND } from "./talent.ts";
import { TOUR_PROGRESS, TOURS_ENABLED } from "./tours.ts";

/** Module (rail hub) ids, in the order the app ships them. Settings → Modules and the rail use these. */
export const MODULE_IDS = ["work", "clients", "money", "people", "calendar", "growth", "reports", "automations"] as const;
export const MODULE_ID = z.enum(MODULE_IDS);
export type ModuleId = z.infer<typeof MODULE_ID>;

/** The rail layout: modules in order, and the ones taken off the rail (still reachable from the Everything page). */
export const RAIL_LAYOUT = z.strictObject({
  order: z.array(MODULE_ID).max(MODULE_IDS.length),
  hidden: z.array(MODULE_ID).max(MODULE_IDS.length).default([]),
});
export type RailLayout = z.infer<typeof RAIL_LAYOUT>;

/** A person's rail: their order and hidden list, plus the modules they pinned to the top (X21-RESB-11). A pinned module
 *  keeps its place in the order when it is unpinned; only the marker goes. */
export const PERSON_RAIL_LAYOUT = RAIL_LAYOUT.extend({
  pinned: z.array(MODULE_ID).max(MODULE_IDS.length).default([]),
});
export type PersonRailLayout = z.infer<typeof PERSON_RAIL_LAYOUT>;

/** The workspace's module setup (Settings → Modules): default rail order, modules turned off for everyone, and the
 *  names the agency gives its hubs (an empty or missing name keeps the shipped one). */
export const MODULES_SETTING = RAIL_LAYOUT.extend({
  labels: z.partialRecord(MODULE_ID, z.string().trim().max(40)).default({}),
});
export type ModulesSetting = z.infer<typeof MODULES_SETTING>;

/** Payment reminders (Settings → Money, PHASED-PLAN L11): each step is days relative to the due date (negative =
 *  before it). The nth reminder of an unpaid invoice goes out on its nth step; past the last step nothing more is sent. */
export const DUNNING_SETTING = z.strictObject({
  enabled: z.boolean(),
  steps: z.array(z.number().int().min(-30).max(120)).max(8).refine((a) => a.every((d, i) => i === 0 || d > a[i - 1]!), "Steps must go up, one per day at most once."),
  /** The wording of each step (X6-08b): index i is step i; null or missing keeps the template or the standard text. */
  messages: z.array(z.object({ subject: z.string().trim().max(200), body: z.string().trim().max(4000) }).nullable()).max(8).optional(),
});
export type DunningSetting = z.infer<typeof DUNNING_SETTING>;
export const DEFAULT_DUNNING: DunningSetting = { enabled: true, steps: [-3, 1, 7, 14] };

/** Lead time of the reminders for due dates (X21-RESA-04): a project milestone reminds its owner this many days before its
 *  due date, a task reminds its assignee this many days before its due date. 0 means on the due date itself. */
export const REMINDER_LEAD = z.strictObject({ milestoneDays: z.number().int().min(0).max(30), taskDays: z.number().int().min(0).max(30) });
export type ReminderLead = z.infer<typeof REMINDER_LEAD>;
export const DEFAULT_REMINDER_LEAD: ReminderLead = { milestoneDays: 2, taskDays: 1 };

/** What runs when a proposal is accepted (statement of work, signers, deposit invoice, plans, deal won): "code" is the
 *  built-in sequence every workspace had before workflows; "workflow" runs the workspace's own "Proposal accepted" workflow,
 *  which it can edit, version and pause. A workspace with no saved value is on "code"; a new workspace starts on "workflow". */
export const REVENUE_LOOP = z.enum(["code", "workflow"]);
export type RevenueLoop = z.infer<typeof REVENUE_LOOP>;
export const DEFAULT_REVENUE_LOOP: RevenueLoop = "code";
/** The `recipeId` of the workspace's built-in "Proposal accepted" workflow, which is how the accept path finds it after a rename. */
export const REVENUE_LOOP_RECIPE = "builtin:proposal-accepted";

/** Client nudges (PHASED-PLAN L5/L17): reminders for what a client owes other than invoices (a signature, an answer to a
 *  proposal, onboarding items). Each step is days since the item opened; one nudge per step. */
export const NUDGES_SETTING = z.strictObject({
  enabled: z.boolean(),
  afterDays: z.array(z.number().int().min(1).max(60)).max(6).refine((a) => a.every((d, i) => i === 0 || d > a[i - 1]!), "Steps must go up."),
});
export type NudgesSetting = z.infer<typeof NUDGES_SETTING>;

/** Order rules (X21-RESB-03). autoApprove: a paid order moves to in-progress at once, even when no project starts.
 *  lockOnDelivered: a delivered order locks until an owner or admin unlocks it. Both are off until the agency turns them
 *  on, so staff keep moving a delivered order back to in-progress as before. */
export const ORDER_RULES = z.strictObject({ autoApprove: z.boolean(), lockOnDelivered: z.boolean() });
export type OrderRules = z.infer<typeof ORDER_RULES>;
export const DEFAULT_ORDER_RULES: OrderRules = { autoApprove: false, lockOnDelivered: false };
export const DEFAULT_NUDGES: NudgesSetting = { enabled: true, afterDays: [2, 5, 9] };

/** How long a client has to finish onboarding intake (Settings, Onboarding): whole days, calendar or business days (X9-35a). */
export const ONBOARDING_TARGETS = z.strictObject({ intakeDays: z.number().int().min(1).max(30), businessDays: z.boolean() });
/** The internal project that holds a staff run's account and equipment tasks (X13-1409a); set by the server on the first run. */
export const INTERNAL_PROJECT_ID = z.string().min(1).max(80).nullable();
export type OnboardingTargets = z.infer<typeof ONBOARDING_TARGETS>;
export const DEFAULT_ONBOARDING_TARGETS: OnboardingTargets = { intakeDays: 3, businessDays: false };

/** Estimated minutes per first-run checklist step (A037, X13-0204a): whole minutes from 1 to 240 for each of the nine steps.
 *  The shipped values are estimates the team has not confirmed; they stay the defaults until an owner or admin changes them. */
const FIRST_RUN_MINUTE = z.number().int().min(1).max(240);
export const FIRST_RUN_MINUTES = z.strictObject({
  mailbox: FIRST_RUN_MINUTE,
  team: FIRST_RUN_MINUTE,
  client: FIRST_RUN_MINUTE,
  project: FIRST_RUN_MINUTE,
  proposal: FIRST_RUN_MINUTE,
  payments: FIRST_RUN_MINUTE,
  brand: FIRST_RUN_MINUTE,
  workflow: FIRST_RUN_MINUTE,
  kickoff: FIRST_RUN_MINUTE,
});
export type FirstRunMinutes = z.infer<typeof FIRST_RUN_MINUTES>;
export const DEFAULT_FIRST_RUN_MINUTES: FirstRunMinutes = { mailbox: 5, team: 5, client: 3, project: 10, proposal: 15, payments: 10, brand: 20, workflow: 15, kickoff: 5 };

/** Agreement reminders and expiry alerts (X6-13b). `everyDays` is the gap between automatic sign-link reminders to the next
 *  signer; `max` caps the reminders per signer (the first sign link is the request, not a reminder, and reminders sent by
 *  hand count too; 0 means none); `expiryDaysBefore` tells the client's owner that an agreement out for signature or signed
 *  expires within that many days (0 turns the alert off). */
export const AGREEMENT_REMINDERS = z.strictObject({
  everyDays: z.number().int().min(1).max(30),
  max: z.number().int().min(0).max(20),
  expiryDaysBefore: z.number().int().min(0).max(90),
});
export type AgreementReminders = z.infer<typeof AGREEMENT_REMINDERS>;
export const DEFAULT_AGREEMENT_REMINDERS: AgreementReminders = { everyDays: 3, max: 5, expiryDaysBefore: 7 };

/** One entry of an agency-defined list (PHASED-PLAN F1): the agency names, colours, orders and archives entries; ids
 *  never change once used, so records keep pointing at them. Archived entries stay readable but are not offered. */
const LIST_ENTRY = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  label: z.string().trim().min(1).max(40),
  hue: HUE,
  archived: z.boolean().default(false),
});
const uniqueIds = (a: { id: string }[]) => new Set(a.map((x) => x.id)).size === a.length;

/** Follow-up sequences (X7-08e): the agency's own list of steps a deal can join. `afterDays` counts from the day the deal joined
 *  the sequence; a step is a reminder to the deal's owner, and the steps must come in day order. Declared after `uniqueIds`. */
export const FOLLOW_UP_STEP = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  afterDays: z.number().int().min(1).max(365),
  kind: z.enum(["email", "call", "message", "task"]),
  note: z.string().trim().max(300),
});
export const FOLLOW_UP_SEQUENCE = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  name: z.string().trim().min(1).max(60),
  steps: z.array(FOLLOW_UP_STEP).min(1).max(12).refine(uniqueIds, "Two steps share an id.").refine((s) => s.every((x, i) => i === 0 || x.afterDays > s[i - 1]!.afterDays), "Each step must come after the one before it."),
});
export const FOLLOW_UP_SEQUENCES = z.array(FOLLOW_UP_SEQUENCE).max(20).refine(uniqueIds, "Two sequences share an id.");
export type FollowUpSequence = z.infer<typeof FOLLOW_UP_SEQUENCE>;

/** Client statuses: each one means a built-in stage (`kind`), so billing, the portal and reports keep working whatever
 *  the agency calls them ("Retainer" and "Project-based" can both be active). */
export const CLIENT_STATUS = LIST_ENTRY.extend({ kind: LIFECYCLE });
export type ClientStatus = z.infer<typeof CLIENT_STATUS>;
export const CLIENT_STATUSES = z.array(CLIENT_STATUS).max(60).refine(uniqueIds, "Two entries share an id.").refine((a) => a.some((x) => !x.archived), "Keep at least one status in use.");
export const DEFAULT_CLIENT_STATUSES: ClientStatus[] = [
  { id: "lead", label: "Lead", hue: "sky", kind: "lead", archived: false },
  { id: "prospect", label: "Prospect", hue: "iris", kind: "prospect", archived: false },
  { id: "onboarding", label: "Onboarding", hue: "saffron", kind: "onboarding", archived: false },
  { id: "active", label: "Active", hue: "fern", kind: "active", archived: false },
  { id: "paused", label: "Paused", hue: "apricot", kind: "paused", archived: false },
  { id: "offboarding", label: "Offboarding", hue: "rose", kind: "offboarding", archived: false },
  { id: "archived", label: "Archived", hue: "ember", kind: "archived", archived: false },
];

/** Plain lists: task types, lead sources, deal loss reasons. */
export const PLAIN_LIST = z.array(LIST_ENTRY).max(60).refine(uniqueIds, "Two entries share an id.");
/** Designations (X21-RESB-07): the agency's titles a person can hold. A person's designationId must be an id on this list. Names up to 80 characters. */
export const DESIGNATIONS = z.array(LIST_ENTRY.extend({ label: z.string().trim().min(1).max(80) })).max(60).refine(uniqueIds, "Two entries share an id.");
export const DEFAULT_DESIGNATIONS: z.infer<typeof DESIGNATIONS> = [];
/** Dynamic groups (X21-OPS-13b): a group whose members are computed live from a rule. A person is in the group when they hold any
 *  of the rule's values in the rule's field: a role (roles.members), a team (kind team) or a department (kind department). */
export const DYNAMIC_GROUP_FIELDS = ["role", "team", "department"] as const;
export const DYNAMIC_GROUP_RULE = z.strictObject({
  field: z.enum(DYNAMIC_GROUP_FIELDS),
  values: z.array(z.string().trim().min(1).max(80)).min(1).max(50),
});
export const DYNAMIC_GROUPS = z
  .array(z.strictObject({ id: z.string().min(1).max(80), name: z.string().trim().min(1).max(80), rule: DYNAMIC_GROUP_RULE }))
  .max(50)
  .refine(uniqueIds, "Two dynamic groups share an id.");
export type DynamicGroup = z.infer<typeof DYNAMIC_GROUPS>[number];
export type DynamicGroupRule = z.infer<typeof DYNAMIC_GROUP_RULE>;
export const DEFAULT_DYNAMIC_GROUPS: DynamicGroup[] = [];
export type ListEntry = z.infer<typeof LIST_ENTRY>;
/** A task type (X9-164a): a list entry that can bring a checklist and an SOP document to every task of that type. A role entry
 *  (X11-527a) in roleChecklists, keyed by a ROLE id (people.ts), replaces them for a task whose first assignee holds that role. */
export const TASK_TYPE = LIST_ENTRY.extend({
  checklist: z.array(z.string().trim().min(1).max(200)).max(30).optional(),
  sopDocId: z.string().max(80).optional(),
  roleChecklists: z.record(z.string(), z.object({ items: z.array(z.string().trim().min(1).max(200)).max(40), sopDocId: z.string().max(80).optional() })).optional(),
});
export const TASK_TYPES = z.array(TASK_TYPE).max(60).refine(uniqueIds, "Two entries share an id.");
export type TaskType = z.infer<typeof TASK_TYPE>;
/** A ticket priority (X11-F1-01): a list entry with a rank. The ticket list sorts by rank, lower first; entries with the same rank
 *  sort by their list position. Ids never change once a ticket saves one; an archived entry stays readable and cannot be chosen again. */
export const TICKET_PRIORITY = LIST_ENTRY.extend({ rank: z.number().int().min(1).max(99) });
export type TicketPriorityEntry = z.infer<typeof TICKET_PRIORITY>;
export const TICKET_PRIORITIES = z.array(TICKET_PRIORITY).max(12).refine(uniqueIds, "Two entries share an id.");
export const DEFAULT_TICKET_PRIORITIES: TicketPriorityEntry[] = [
  { id: "urgent", label: "Urgent", hue: "ember", rank: 1, archived: false },
  { id: "high", label: "High", hue: "apricot", rank: 2, archived: false },
  { id: "medium", label: "Medium", hue: "saffron", rank: 3, archived: false },
  { id: "low", label: "Low", hue: "sky", rank: 4, archived: false },
  { id: "none", label: "No priority", hue: "lagoon", rank: 5, archived: false },
];
export const DEFAULT_TASK_TYPES: ListEntry[] = ["Design", "Copy", "Development", "SEO", "Ads", "Video", "Admin"].map((label, i) => ({ id: label.toLowerCase(), label, hue: (["iris", "orchid", "cobalt", "fern", "saffron", "rose", "lagoon"] as const)[i]!, archived: false }));
export const DEFAULT_LEAD_SOURCES: ListEntry[] = ["Referral", "Website", "Outreach", "Event", "Partner", "Social"].map((label, i) => ({ id: label.toLowerCase(), label, hue: (["fern", "sky", "cobalt", "saffron", "iris", "orchid"] as const)[i]!, archived: false }));
/** The departments a doc belongs to (X9-168a): ids never change once used; the five starting departments. */
export const DEFAULT_DOC_DEPARTMENTS: ListEntry[] = ([["delivery", "Delivery", "cobalt"], ["sales", "Sales", "fern"], ["finance", "Finance", "saffron"], ["people", "People", "iris"], ["brand", "Brand", "apricot"]] as const).map(([id, label, hue]) => ({ id, label, hue, archived: false }));
/** A hiring stage (X9-169a): an agency-named entry whose `kind` gives it the built-in meaning the hiring rules use. Two stages
 *  may share a kind (two screens); the rules need at least one live stage of the kinds applied, interview, hired and rejected. */
const CANDIDATE_STAGE_ENTRY = LIST_ENTRY.extend({ kind: CANDIDATE_STAGE_KIND });
export type CandidateStageEntry = z.infer<typeof CANDIDATE_STAGE_ENTRY>;
export const CANDIDATE_STAGES = z.array(CANDIDATE_STAGE_ENTRY).max(30).refine(uniqueIds, "Two entries share an id.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "applied"), "Keep at least one Applied stage.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "interview"), "Keep at least one Interview stage.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "hired"), "Keep at least one Hired stage.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "rejected"), "Keep at least one Rejected stage.");
export const DEFAULT_CANDIDATE_STAGES: CandidateStageEntry[] = ([["applied", "Applied", "sky"], ["screen", "Screen", "iris"], ["interview", "Interview", "cobalt"], ["offer", "Offer", "saffron"], ["hired", "Hired", "fern"], ["rejected", "Rejected", "ember"]] as const).map(([id, label, hue]) => ({ id, label, hue, kind: id, archived: false }));
/** Agreement types (X10-62a): the agency's own list (e.g. "Retainer terms", "Freelancer NDA"). Each entry's `kind` is the
 *  built-in meaning the agreement rules read (a statement of work under a signed master, an MSA's children); the list keeps a
 *  live type of each kind. Ids never change once used, so agreements keep pointing at them. */
export const AGREEMENT_TYPE_KIND = z.enum(["msa", "sow", "nda", "change-request"]);
export const AGREEMENT_TYPE = LIST_ENTRY.extend({ kind: AGREEMENT_TYPE_KIND });
export type AgreementType = z.infer<typeof AGREEMENT_TYPE>;
export const AGREEMENT_TYPES = z.array(AGREEMENT_TYPE).max(40).refine(uniqueIds, "Two entries share an id.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "msa"), "Keep at least one master type.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "sow"), "Keep at least one statement of work type.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "nda"), "Keep at least one NDA type.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "change-request"), "Keep at least one change request type.");
export const DEFAULT_AGREEMENT_TYPES: AgreementType[] = ([["msa", "MSA", "iris"], ["sow", "SOW", "cobalt"], ["nda", "NDA", "sky"], ["change-request", "Change request", "saffron"]] as const).map(([id, label, hue]) => ({ id, label, hue, kind: id, archived: false }));
/** Ticket statuses (X11-306a): the agency's own list. Each entry's `kind` is the built-in meaning the ticket rules read: an open
 *  status keeps the SLA clock running, a solved status stops it and asks the client for CSAT once. Ids never change once used, so
 *  tickets keep pointing at them; an archived status stays readable on old tickets but cannot be chosen again. */
export const TICKET_STATUS_KIND = z.enum(["open", "solved"]);
export const TICKET_STATUS_ENTRY = LIST_ENTRY.extend({ kind: TICKET_STATUS_KIND });
export type TicketStatusEntry = z.infer<typeof TICKET_STATUS_ENTRY>;
export const TICKET_STATUSES = z.array(TICKET_STATUS_ENTRY).max(30).refine(uniqueIds, "Two entries share an id.")
  .refine((a) => a.some((x) => !x.archived && x.kind === "solved"), "Keep at least one solved status in use.");
export const DEFAULT_TICKET_STATUSES: TicketStatusEntry[] = [
  { id: "new", label: "New", hue: "sky", kind: "open", archived: false },
  { id: "open", label: "Open", hue: "iris", kind: "open", archived: false },
  { id: "pending", label: "Pending", hue: "saffron", kind: "open", archived: false },
  { id: "on-hold", label: "On hold", hue: "apricot", kind: "open", archived: false },
  { id: "solved", label: "Solved", hue: "fern", kind: "solved", archived: false },
];
/** Credential kinds (X11-537a): what a credential is (Login, API key, Domain). The agency names, colours and archives them; a
 *  credential carries one id from this list. Ids never change once used. An archived kind stays readable on the credentials
 *  that carry it, and cannot be chosen for a new one. At least one kind stays in use, so a credential can always be added. */
export const CREDENTIAL_KINDS = z.array(LIST_ENTRY).max(40).refine(uniqueIds, "Two entries share an id.")
  .refine((a) => a.some((x) => !x.archived), "Keep at least one credential kind in use.");
export const DEFAULT_CREDENTIAL_KINDS: ListEntry[] = [
  { id: "login", label: "Login", hue: "sky", archived: false },
  { id: "api-key", label: "API key", hue: "iris", archived: false },
  { id: "ssh", label: "SSH key", hue: "lagoon", archived: false },
  { id: "domain", label: "Domain", hue: "fern", archived: false },
  { id: "hosting", label: "Hosting", hue: "apricot", archived: false },
  { id: "database", label: "Database", hue: "cobalt", archived: false },
  { id: "social", label: "Social", hue: "rose", archived: false },
];
export const DEFAULT_LOSS_REASONS: ListEntry[] =["Price", "Timing", "Chose a competitor", "No budget", "No response"].map((label, i) => ({ id: label.toLowerCase().replace(/\s+/g, "-"), label, hue: (["ember", "apricot", "rose", "saffron", "sky"] as const)[i]!, archived: false }));
/** The job roles the agency staffs by (X9-12a): the starting list until the agency saves its own (setting teamRoles). */
export const DEFAULT_TEAM_ROLES: ListEntry[] = ([["operations", "Operations", "sky"], ["account-manager", "Account manager", "lagoon"], ["project-manager", "Project manager", "cobalt"], ["designer", "Designer", "iris"], ["developer", "Developer", "jade"], ["copywriter", "Copywriter", "orchid"], ["seo", "SEO", "fern"], ["ppc", "PPC", "saffron"], ["social", "Social", "rose"], ["video", "Video", "ember"], ["qa", "QA", "citron"], ["finance", "Finance", "apricot"], ["hr", "HR", "sky"], ["sales", "Sales", "lagoon"]] as const).map(([id, label, hue]) => ({ id, label, hue, archived: false }));

/** One custom field (PHASED-PLAN F2). `options` for select and multi-select; `required` is checked when a record is
 *  created; `clientVisible` shows it in the portal. Field ids never change once saved (values are keyed by them).
 *  formula, relation and rollup are calculated or linked fields (A075): `formula` is an expression over the record's
 *  numeric fields (contracts/expr), `relationTo` the object a relation points at, and `rollup` adds up a numeric field
 *  over the records related through `via` (a relation field on the same object), or over subtasks when `via` is left out. */
export const FIELD_TYPE = z.enum(["text", "long-text", "number", "money", "date", "select", "multi-select", "person", "checkbox", "url", "email", "phone", "formula", "rollup", "relation"]);
export const RELATION_TARGET = z.enum(["clients", "deals", "projects", "tasks"]);
export const ROLLUP_FN = z.enum(["SUM", "AVG", "MIN", "MAX", "COUNT"]);
export const FIELD_DEF = z.strictObject({
  id: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
  label: z.string().trim().min(1).max(60),
  type: FIELD_TYPE,
  options: z.array(z.strictObject({ id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/), label: z.string().trim().min(1).max(40), hue: HUE })).max(100).optional(),
  required: z.boolean().default(false),
  clientVisible: z.boolean().default(false),
  archived: z.boolean().default(false),
  help: z.string().max(200).optional(),
  /** Optional group label (X11-102a): fields that share a group are shown under one heading; absent = no group. */
  group: z.string().trim().min(1).max(40).optional(),
  /** Tasks only (X9-164a): the task type ids this field belongs to; absent or empty = every task. */
  taskTypes: z.array(z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/)).max(60).optional(),
  /** The asset class the field's value carries (X11-530a): a class id of the managed list. Absent: none. */
  assetClass: ASSET_CLASS_ID.optional(),
  formula: z.string().max(500).optional(),
  relationTo: RELATION_TARGET.optional(),
  rollup: z.strictObject({ via: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/).optional(), field: z.string().min(1).max(40), fn: ROLLUP_FN }).optional(),
});
export type FieldDef = z.infer<typeof FIELD_DEF>;
export const CUSTOM_OBJECTS = ["clients", "contacts", "deals", "projects", "tasks", "invoices"] as const;
const FIELDS = z.array(FIELD_DEF).max(80).refine(uniqueIds, "Two fields share an id.");
/** Native numeric columns a formula or rollup may read on an object, besides its custom number fields. */
const NATIVE_NUMBERS: Partial<Record<(typeof CUSTOM_OBJECTS)[number], string[]>> = { tasks: ["estimateH", "loggedH"] };

/** Checks the calculated and linked fields of one object: a formula must parse and read only numbers of the object, a
 *  relation names its target, and a rollup adds up a number through a self relation (or subtasks, on tasks). */
function checkCalculated(o: (typeof CUSTOM_OBJECTS)[number], defs: FieldDef[], ctx: z.RefinementCtx): void {
  const numbers = [...defs.filter((d) => d.type === "number" || d.type === "money").map((d) => d.id), ...(NATIVE_NUMBERS[o] ?? [])];
  // a formula may also read the rollups of its record; a rollup adds up plain numbers only
  const readable = [...numbers, ...defs.filter((d) => d.type === "rollup").map((d) => d.id)];
  defs.forEach((d, i) => {
    const issue = (message: string, path: string) => ctx.addIssue({ code: "custom", message, path: [o, i, path] });
    if (d.type === "formula") {
      if (!d.formula) return issue("A formula field needs a formula.", "formula");
      const problem = checkFormula(d.formula, readable);
      if (problem) issue(problem, "formula");
    }
    if (d.type === "relation" && !d.relationTo) issue("A relation field needs the object it points at.", "relationTo");
    if (d.type === "rollup") {
      if (!d.rollup) return issue("A rollup field needs a number to add up.", "rollup");
      if (d.rollup.via) {
        const via = defs.find((x) => x.id === d.rollup!.via);
        if (!via || via.type !== "relation" || via.relationTo !== o) issue("Pick a relation field that points at this object.", "rollup");
      } else if (o !== "tasks") issue("Without a relation field, a rollup adds up subtasks, which only tasks have.", "rollup");
      if (!numbers.includes(d.rollup.field)) issue("Pick a number field to add up.", "rollup");
    }
  });
}

export const CUSTOM_FIELDS = z
  .strictObject(Object.fromEntries(CUSTOM_OBJECTS.map((o) => [o, FIELDS.optional()])) as Record<(typeof CUSTOM_OBJECTS)[number], z.ZodOptional<typeof FIELDS>>)
  .superRefine((value, ctx) => {
    for (const o of CUSTOM_OBJECTS) checkCalculated(o, (value as Record<string, FieldDef[] | undefined>)[o] ?? [], ctx);
  });
export type CustomFields = z.infer<typeof CUSTOM_FIELDS>;

/** Time rules (Settings → Time, PHASED-PLAN F4), used by the timer, timesheets and approvals on the server. */
export const TIME_RULES = z.strictObject({
  /** Timer stops round to this many minutes (1 = no rounding). */
  roundTo: z.number().int().min(1).max(60),
  roundDirection: z.enum(["nearest", "up", "down"]),
  /** Entries older than this many days cannot be changed (0 = never locked). */
  lockAfterDays: z.number().int().min(0).max(365),
  /** A person cannot approve their own timesheet. */
  noSelfApproval: z.boolean(),
  /** Approving a day's time is refused until one of its entries has a note (X14-1601a). Optional, default off. */
  requireDailyNote: z.boolean().optional(),
  /** Fields every time entry must carry before it saves (X19-2301a). Optional, default none. */
  requiredFields: z.array(z.enum(["project", "task", "tag", "description"])).max(4).optional(),
  /** Admin lock date (X19-2301a): entries on or before it can be created, edited or deleted only by their owner. Optional, default none. */
  lockDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  hoursPerDay: z.number().min(1).max(24),
  hoursPerWeek: z.number().min(1).max(168),
  /** Several timers at once (FEATURES: Timer rules). Optional, default off: one running timer per person. */
  allowMultipleTimers: z.boolean().optional(),
  /** Timesheet cutoff (FEATURES: Timesheet routine): ISO weekday, 1 = Monday to 7 = Sunday. Optional, default 5 (Friday). */
  cutoffWeekday: z.number().int().min(1).max(7).optional(),
  /** Cutoff time of day in the workspace time zone, "HH:MM". Optional, default "17:00". */
  cutoffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
  /** Percent of people whose week is in by the cutoff. Optional, default 95. */
  complianceTarget: z.number().int().min(0).max(100).optional(),
  /** A running timer older than this many hours is reported as stale. Optional, default 12. */
  staleTimerHours: z.number().min(1).max(72).optional(),
  /** Minutes suggested for each task a person touched on a day, until they confirm or change it. Optional, default 30. */
  suggestMinutes: z.number().int().min(15).max(480).optional(),
  /** Exceptions (Settings → Time): a role's or a person's rules where they differ from the workspace rules. A role override
   *  names a roleId, a person override a personId; an empty field follows the workspace rule. Optional, default none. */
  overrides: z
    .array(
      z.strictObject({
        personId: z.string().optional(),
        roleId: z.string().optional(),
        roundTo: z.number().int().min(1).max(60).optional(),
        roundDirection: z.enum(["nearest", "up", "down"]).optional(),
        lockAfterDays: z.number().int().min(0).max(365).optional(),
        noSelfApproval: z.boolean().optional(),
      }),
    )
    .max(50)
    .optional(),
});
export type TimeRules = z.infer<typeof TIME_RULES>;
export const DEFAULT_TIME_RULES: TimeRules = { roundTo: 15, roundDirection: "nearest", lockAfterDays: 0, noSelfApproval: false, requireDailyNote: false, hoursPerDay: 8, hoursPerWeek: 40 };

/** Invoice numbering (Settings → Money): {client} = client code, {yyyy} = year, {seq} = the next number, padded to
 *  `pad` digits. Numbers are gap-free per workspace and given when an invoice is sent. */
export const NUMBERING = z.strictObject({
  invoice: z.string().trim().min(5).max(40).refine((f) => f.includes("{seq}"), "The format needs {seq}."),
  pad: z.number().int().min(1).max(8),
});
export type Numbering = z.infer<typeof NUMBERING>;
export const DEFAULT_NUMBERING: Numbering = { invoice: "{client}-{seq}", pad: 4 };

/** Payment terms (Settings → Money): days from issue to due for invoices the system raises. */
export const PAYMENT_TERMS = z.strictObject({ days: z.number().int().min(0).max(120) });
export const DEFAULT_PAYMENT_TERMS = { days: 14 };

/** The agency's optional sending relay (X13-0612a): one provider that sends mail over its HTTP API. Connected mailboxes stay the
 *  default; a relay is used only when one is picked and its key is saved. The key is never in this value: the server seals it in
 *  the vault and sets keySaved. */
export const RELAY_PROVIDER = z.enum(["none", "sendgrid", "ses", "mailgun", "postmark", "brevo", "smtp"]);
export const SENDING_RELAY = z.object({
  provider: RELAY_PROVIDER,
  fromAddress: z.string().max(254).default(""),
  region: z.string().trim().max(40).optional(),
  domain: z.string().trim().max(253).optional(),
  keySaved: z.boolean().default(false),
  /** SMTP relay (X21-RESB-13): the server and login. The password is never stored here: `passwordEnv` names the environment variable that holds it. */
  host: z.string().trim().max(253).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  username: z.string().trim().max(254).optional(),
  secure: z.boolean().default(false),
  passwordEnv: z.string().trim().regex(/^[A-Z_][A-Z0-9_]{0,63}$/, "Enter the name of the environment variable that holds the password.").optional(),
}).superRefine((r, ctx) => {
  if (r.provider === "none") return;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.fromAddress)) ctx.addIssue({ code: "custom", path: ["fromAddress"], message: "Enter the from address this relay sends as." });
  if (r.provider === "ses" && !r.region) ctx.addIssue({ code: "custom", path: ["region"], message: "Enter the AWS region, for example eu-west-1." });
  if (r.provider === "mailgun" && !r.domain) ctx.addIssue({ code: "custom", path: ["domain"], message: "Enter the Mailgun sending domain." });
  if (r.provider === "smtp") {
    if (!r.host) ctx.addIssue({ code: "custom", path: ["host"], message: "Enter the SMTP server host." });
    if (!r.port) ctx.addIssue({ code: "custom", path: ["port"], message: "Enter the SMTP port, from 1 to 65535." });
    if (!r.username) ctx.addIssue({ code: "custom", path: ["username"], message: "Enter the SMTP login." });
  }
});
export type SendingRelay = z.infer<typeof SENDING_RELAY>;
export const DEFAULT_SENDING_RELAY: SendingRelay = { provider: "none", fromAddress: "", keySaved: false, secure: false };

/** Time to cash target (X6-16a): the days from acceptance (or issue) to payment the agency aims for. Read by mod-money
 *  forecast.ts time to cash, which reports the share of paid invoices within it. */
export const TIME_TO_CASH_TARGET = z.strictObject({ targetDays: z.number().int().min(1).max(365) });
export const DEFAULT_TIME_TO_CASH_TARGET = { targetDays: 30 };

/** Where clients send a bank transfer (Settings → Money → Payment gateways). Every field is optional. The pay page offers
 *  "Bank transfer" only when an account field is set. `routingLabel` is the name of the local routing code the agency's bank
 *  uses (BSB, sort code, routing number). `referenceRule` is what the client writes as the payment reference: {invoice} is
 *  the invoice number and {client} the client's code. */
export const BANK_TRANSFER = z.strictObject({
  accountName: z.string().trim().max(120).default(""),
  routingLabel: z.string().trim().max(30).default("BSB"),
  routingCode: z.string().trim().max(20).default(""),
  accountNumber: z.string().trim().max(34).default(""),
  iban: z.string().trim().max(34).default(""),
  swift: z.string().trim().max(11).default(""),
  referenceRule: z.string().trim().min(1).max(60).refine((r) => r.includes("{invoice}"), "The reference needs {invoice}, so a payment can be matched to its invoice.").default("{invoice}"),
});
export type BankTransfer = z.infer<typeof BANK_TRANSFER>;
export const DEFAULT_BANK_TRANSFER: BankTransfer = BANK_TRANSFER.parse({});
/** True when the agency has given at least one account detail a client could send money to. */
export const bankTransferIsSet = (b: Pick<BankTransfer, "accountName" | "routingCode" | "accountNumber" | "iban" | "swift">) => [b.accountName, b.routingCode, b.accountNumber, b.iban, b.swift].some((v) => v.trim() !== "");
/** The payment reference a client writes for an invoice: the rule with {invoice} and {client} filled in. */
export const bankReference = (rule: string, invoiceNumber: string, clientCode: string) => rule.replaceAll("{invoice}", invoiceNumber).replaceAll("{client}", clientCode);

/** Payment methods (X13-3501a): one ordered list of the ways a client can pay. The kinds are bank, paypal, crypto, wise and
 *  custom. Each kind's `details` is checked against its own schema in mod-money payment-methods.ts; the bank method's details
 *  are the bankTransfer setting's fields, read there. Public payment details only: no vendor secret is stored here. */
export const PAYMENT_METHOD_KIND = z.enum(["bank", "paypal", "crypto", "wise", "custom"]);
export type PaymentMethodKind = z.infer<typeof PAYMENT_METHOD_KIND>;
const OPTIONAL_EMAIL = z.union([z.literal(""), z.string().trim().max(200).email()]);
export const PAYMENT_METHOD_DETAILS: Record<PaymentMethodKind, z.ZodType> = {
  bank: BANK_TRANSFER.omit({ referenceRule: true }),
  paypal: z.strictObject({
    link: z.union([z.literal(""), z.string().trim().max(300).regex(/^https:\/\/\S+$/, "Use an https link.")]).default(""),
    email: OPTIONAL_EMAIL.default(""),
    note: z.string().trim().max(200).default(""),
  }),
  crypto: z.strictObject({
    coin: z.string().trim().max(10).default(""),
    network: z.string().trim().max(40).default(""),
    address: z.string().trim().max(200).default(""),
    memo: z.string().trim().max(100).default(""),
    showQr: z.boolean().default(false),
  }),
  wise: z.strictObject({ email: OPTIONAL_EMAIL.default(""), accountDetails: z.string().trim().max(1000).default("") }),
  custom: z.strictObject({}),
};
export const PAYMENT_METHOD = z.strictObject({
  id: z.string().trim().regex(/^[a-z0-9-]+$/, "Use lower-case letters, digits and dashes for an id.").min(1).max(40),
  kind: PAYMENT_METHOD_KIND,
  label: z.string().trim().max(60),
  enabled: z.boolean().default(true),
  currencies: z.array(z.string().trim().toUpperCase()).max(20).default([]),
  instructions: z.string().max(1000).default(""),
  sortOrder: z.number().int().min(0).max(999).default(0),
  brandId: z.string().nullable().default(null),
  details: z.record(z.string(), z.unknown()).default({}),
});
export type PaymentMethod = z.infer<typeof PAYMENT_METHOD>;
/** The list and the reference rule the bank transfer and every other method print. Empty by default. */
export const PAYMENT_METHODS = z.strictObject({
  methods: z.array(PAYMENT_METHOD).max(20).default([]),
  referenceRule: z.string().trim().min(1).max(60).refine((r) => r.includes("{invoice}"), "The reference needs {invoice}, so a payment can be matched to its invoice.").default("{invoice}"),
});
export type PaymentMethods = z.infer<typeof PAYMENT_METHODS>;
export const DEFAULT_PAYMENT_METHODS: PaymentMethods = PAYMENT_METHODS.parse({});

/** Payer-side processing fee (X9-05a): what a client adds on top of the part of an invoice they pay by card, per gateway.
 *  `pct` is a percentage of that part (0 to 10); `fixed` is an amount in the workspace currency (0 to 100) that applies
 *  only to invoices in that currency. 0 and 0 is off. Surcharging cards is restricted in some countries (see the card). */
export const PAYER_FEE = z.strictObject({ pct: z.number().min(0).max(10).default(0), fixed: z.number().min(0).max(100).default(0) });
export const PAYER_FEES = z.strictObject({ stripe: PAYER_FEE.default({ pct: 0, fixed: 0 }), paypal: PAYER_FEE.default({ pct: 0, fixed: 0 }) });
export type PayerFee = z.infer<typeof PAYER_FEE>;
export type PayerFees = z.infer<typeof PAYER_FEES>;
export const DEFAULT_PAYER_FEES: PayerFees = PAYER_FEES.parse({});
/** The fee in minor units for paying `amountMinor`: the percentage (rounded) plus the fixed part when it applies. Nothing for a zero amount. */
export function payerFeeMinor(amountMinor: number, fee: PayerFee, o: { exponent: number; fixedApplies: boolean }): number {
  if (!(amountMinor > 0)) return 0;
  return Math.round((amountMinor * fee.pct) / 100) + (o.fixedApplies ? Math.round(fee.fixed * 10 ** o.exponent) : 0);
}

/** The invoice PDF (Settings → Money → Document templates, X5-04a): one of three layouts, whether the logo and the bank
 *  details show on it, and a footer note printed above the company line. */
export const INVOICE_BLOCK = z.enum(["header", "client", "lines", "totals", "payment", "notes", "footer"]);
export type InvoiceBlock = z.infer<typeof INVOICE_BLOCK>;
export const INVOICE_DOCUMENT = z.strictObject({
  template: z.enum(["classic", "modern", "compact"]),
  showLogo: z.boolean(),
  showPaymentDetails: z.boolean(),
  footerNote: z.string().max(500),
  /** The order of the invoice's blocks (X13-0510a): header (invoice dates and purchase order), client (billed to), lines, totals,
   *  payment (bank details, when shown), notes, footer (the footer note). Each block prints once, in the order given; a block
   *  left out does not print. Absent means the built-in order. */
  blocks: z.array(INVOICE_BLOCK).min(1, "Keep at least one block on the invoice.").max(7).optional(),
});
export type InvoiceDocument = z.infer<typeof INVOICE_DOCUMENT>;
export const DEFAULT_INVOICE_DOCUMENT: InvoiceDocument = { template: "classic", showLogo: true, showPaymentDetails: true, footerNote: "" };

/** Company details (Settings → Workspace), shown on invoices, proposals and emails. */
export const COMPANY = z.strictObject({
  legalName: z.string().trim().max(120).default(""),
  address: z.string().trim().max(400).default(""),
  taxId: z.string().trim().max(60).default(""),
  website: z.string().trim().max(200).default(""),
  phone: z.string().trim().max(40).default(""),
  email: z.string().trim().max(200).default(""),
});
export type Company = z.infer<typeof COMPANY>;

const DAY_MS = 86_400_000;
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use 24-hour time, such as 09:00.");
const REAL_DATE = ISO_DATE.refine((d) => new Date(`${d}T00:00:00Z`).toISOString().slice(0, 10) === d, "Use a real date.");

/** Formats (Settings → Workspace → Locale): the language tag that dates and numbers follow (Intl writes the date order and
 *  the number shapes from it), the time zone, and an optional 12- or 24-hour clock. Until it is saved, each person's
 *  browser setting applies. */
const isLanguageTag = (tag: string) => {
  try {
    return Intl.getCanonicalLocales(tag).length === 1;
  } catch {
    return false;
  }
};
const isTimeZone = (zone: string) => {
  try {
    return Intl.DateTimeFormat("en-US", { timeZone: zone }).resolvedOptions().timeZone.length > 0;
  } catch {
    return false;
  }
};
export const LOCALE = z.strictObject({
  language: z.string().trim().min(2).max(35).refine(isLanguageTag, "Use a language tag such as en-AU."),
  timeZone: z.string().trim().min(1).max(64).refine(isTimeZone, "Pick a time zone such as Australia/Sydney."),
  hour12: z.boolean(),
});
export type Locale = z.infer<typeof LOCALE>;

/** The currency amounts are also shown in, beside their own (display only; rates come from the FX feed). Null shows each
 *  amount as it is. */
export const DISPLAY_CURRENCY = z.strictObject({
  code: z.string().regex(/^[A-Z]{3}$/, "Use a three-letter currency code such as AUD.").nullable(),
});
export type DisplayCurrency = z.infer<typeof DISPLAY_CURRENCY>;

/** Holiday countries (Settings → Workspace → Hours). Each lists the national holidays that fall on the same calendar date
 *  every year. Movable holidays and state or regional days go in the workspace's own list. Weekend holidays are not moved. */
export const HOLIDAY_COUNTRIES = ["none", "AU", "NZ", "US", "GB", "CA"] as const;
export type HolidayCountry = (typeof HOLIDAY_COUNTRIES)[number];
const FIXED_HOLIDAYS: Record<Exclude<HolidayCountry, "none">, [month: number, day: number, label: string][]> = {
  AU: [[1, 1, "New Year's Day"], [1, 26, "Australia Day"], [4, 25, "ANZAC Day"], [12, 25, "Christmas Day"], [12, 26, "Boxing Day"]],
  NZ: [[1, 1, "New Year's Day"], [1, 2, "Day after New Year's Day"], [2, 6, "Waitangi Day"], [4, 25, "ANZAC Day"], [12, 25, "Christmas Day"], [12, 26, "Boxing Day"]],
  US: [[1, 1, "New Year's Day"], [7, 4, "Independence Day"], [11, 11, "Veterans Day"], [12, 25, "Christmas Day"]],
  GB: [[1, 1, "New Year's Day"], [12, 25, "Christmas Day"], [12, 26, "Boxing Day"]],
  CA: [[1, 1, "New Year's Day"], [7, 1, "Canada Day"], [12, 25, "Christmas Day"], [12, 26, "Boxing Day"]],
};

/** The working calendar (Settings → Workspace → Hours): ISO weekdays (1 Monday to 7 Sunday), the day's start and end,
 *  the holiday country and the workspace's own holidays. */
export const WORKING_HOURS = z
  .strictObject({
    days: z.array(z.number().int().min(1).max(7)).max(7).refine((d) => new Set(d).size === d.length, "List each day once."),
    start: HHMM,
    end: HHMM,
    country: z.enum(HOLIDAY_COUNTRIES),
    holidays: z.array(z.strictObject({ date: REAL_DATE, label: z.string().trim().min(1).max(60) })).max(200),
  })
  .refine((w) => w.end > w.start, { message: "The day ends after it starts.", path: ["end"] });
export type WorkingHours = z.infer<typeof WORKING_HOURS>;
export const DEFAULT_WORKING_HOURS: WorkingHours = { days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00", country: "none", holidays: [] };

/** Cost model (Settings → Money → Cost rates, X2-12): the overhead share and the yearly billable hours the cost rate
 *  calculator starts from. A person's own row can differ. */
/** How the overhead pool is spread over people (X7-02g): by billable hours, by headcount, or by billing value. */
export const OVERHEAD_BASIS = z.enum(["hours", "headcount", "revenue"]);
export type OverheadBasis = z.infer<typeof OVERHEAD_BASIS>;
export const COST_MODEL = z.strictObject({
  overheadPct: z.number().min(0).max(500),
  billableHours: z.number().positive().max(4000),
  /** The yearly overhead entered once and how it is spread over people (X7-02g). Absent: the last twelve months' actual overhead, by hours. */
  overheadPool: z.strictObject({ annual: z.number().min(0).max(1_000_000_000), basis: OVERHEAD_BASIS }).optional(),
});
export type CostModel = z.infer<typeof COST_MODEL>;
export const DEFAULT_COST_MODEL: CostModel = { overheadPct: 25, billableHours: 1400 };
/** The agency's markup on external ad spend shown to clients, in percent (X5-09). Applied once, to client-facing reads only. */
export const AD_SPEND_MARKUP = z.strictObject({ pct: z.number().min(0).max(100) });
export const DEFAULT_AD_SPEND_MARKUP = { pct: 0 };
/** The workspace tax rate in basis points (1000 = 10%), written by an owner's apply of a region (X13-1208b). null: no rate set. */
export const TAX_RATE_BASIS_POINTS = z.strictObject({ basisPoints: z.number().int().min(0).max(10000).nullable() });
export const DEFAULT_TAX_RATE_BASIS_POINTS = { basisPoints: null };
/** Whether deposit and instalment invoices carry tax (X13-3202a). Off: they carry no tax rule and no workspace rate. */
export const CHARGE_TAX_ON_DEPOSITS = z.strictObject({ on: z.boolean() });
export const DEFAULT_CHARGE_TAX_ON_DEPOSITS = { on: true };
/** The legal clauses a region's defaults add, written by an owner's apply of a region (X13-1208b). */
export const LEGAL_CLAUSES = z.strictObject({ clauses: z.array(z.string().trim().min(1).max(500)).max(20) });
export const DEFAULT_LEGAL_CLAUSES = { clauses: [] as string[] };
/** Two-way sync with Xero (X5-15) and QuickBooks (X5-28): whether it runs, the account payments are booked to, the first issue date it sends, and the QuickBooks service item lines are booked to. */
export const ACCOUNTING_SYNC = z.strictObject({
  enabled: z.boolean(),
  accountCode: z.string().trim().min(1).max(20),
  from: ISO_DATE.optional(),
  qboItemId: z.string().trim().min(1).max(20).optional(),
  /** Xero sales account for lines (X6-08b): AccountCode of a line = lineAccounts[kind] ?? salesAccountCode. */
  salesAccountCode: z.string().trim().min(1).max(20).optional(),
  lineAccounts: z.partialRecord(z.enum(["service", "time", "expense", "discount", "fee", "milestone"]), z.string().trim().min(1).max(20)).optional(),
});
export const DEFAULT_ACCOUNTING_SYNC = { enabled: true, accountCode: "090" };

const ascending = (a: number[]) => a.every((v, i) => i === 0 || v > a[i - 1]!);

/** Budget alerts (Settings → Money → Budget alerts, X2-12): a project's PM is told when its budget used passes each step
 *  (percent of budget) and when its margin falls below the target. The same target is the margin a proposal line with
 *  no known cost is assumed to earn. */
export const BUDGET_ALERTS = z.strictObject({
  enabled: z.boolean(),
  burnSteps: z.array(z.number().int().min(10).max(300)).max(6).refine(ascending, "Steps must go up."),
  marginTargetPct: z.number().min(0).max(99),
});
export type BudgetAlerts = z.infer<typeof BUDGET_ALERTS>;
export const DEFAULT_BUDGET_ALERTS: BudgetAlerts = { enabled: true, burnSteps: [75, 90, 100], marginTargetPct: 40 };

/** Revenue recognition method per budget type (Settings → Money → Revenue recognition, X6-16b). `percent-complete`: the budget
 *  by the project's progress; `milestones`: the amount of each done milestone; `billed`: what the ledger holds for the project's
 *  invoices; `time-and-materials`: the billable time at its rates (capped at the budget for `capped`); `monthly`: the budget by
 *  the calendar month (retainers). Read by finance_recognized (packages/db/sql/R-55-finance.sql); the defaults there match these. */
export const RECOGNITION_METHOD = z.enum(["percent-complete", "milestones", "billed", "time-and-materials", "monthly"]);
export type RecognitionMethod = z.infer<typeof RECOGNITION_METHOD>;
export const RECOGNITION = z.record(BUDGET_TYPE, RECOGNITION_METHOD);
/** Today's behaviour for each budget type, so an unset workspace changes nothing. */
export const DEFAULT_RECOGNITION: z.infer<typeof RECOGNITION> = {
  fixed: "percent-complete", "time-materials": "time-and-materials", capped: "time-and-materials", retainer: "monthly", milestone: "percent-complete", outcome: "percent-complete",
};

/** The month-end close checklist template (X11-621a): the steps a finance record starts with, in order. `ownerId` is optional:
 *  a step without one starts unassigned. Read by mod-money recognition.ts when a finance record is first created; an empty
 *  list starts the record with no steps. */
export const CLOSE_TEMPLATE_STEP = z.strictObject({ label: z.string().trim().min(1).max(160), ownerId: z.string().max(80).optional() });
export type CloseTemplateStep = z.infer<typeof CLOSE_TEMPLATE_STEP>;
export const CLOSE_TEMPLATE = z.array(CLOSE_TEMPLATE_STEP).max(40);
export const DEFAULT_CLOSE_TEMPLATE: CloseTemplateStep[] = [
  { label: "Reconcile the bank accounts." },
  { label: "Post accruals for finished work." },
  { label: "Review work in progress." },
  { label: "Confirm the invoice run." },
];

/** The weekly capacity digest (X6-14b, Settings → Notifications): on Monday mornings in the workspace time zone, owners, admins
 *  and PMs get the next four weeks' capacity, booked hours and likely demand. Read by mod-people digest.ts. */
export const CAPACITY_DIGEST = z.strictObject({ enabled: z.boolean() });
export type CapacityDigest = z.infer<typeof CAPACITY_DIGEST>;
export const DEFAULT_CAPACITY_DIGEST: CapacityDigest = { enabled: true };
/** The hiring and freelancer suggestion (X9-12b, Settings → Team): a job role whose booked hours and open placeholders pass
 *  utilisationPct of its holders' capacity for weeks weeks ahead gets a suggestion; sustainedWeeks over-limit weeks mean hire. */
export const HIRING_RULES = z.strictObject({ enabled: z.boolean(), utilisationPct: z.number().min(50).max(150), weeks: z.number().int().min(1).max(26), sustainedWeeks: z.number().int().min(1).max(26), roleLimits: z.record(z.string().regex(/^[a-z0-9-]{1,40}$/), z.number().min(50).max(150)).default({}) });
export type HiringRules = z.infer<typeof HIRING_RULES>;
export const DEFAULT_HIRING_RULES: HiringRules = { enabled: true, utilisationPct: 85, weeks: 8, sustainedWeeks: 4, roleLimits: {} };
/** The utilisation band the agency aims for (X11-623a): the default band is 70 to 80 percent, and a role band wins over it for
 *  the people who hold that role. Each capacity row reports under, in or over its band. Set through PUT /settings/utilisationTargets. */
const UTILISATION_BAND = z.strictObject({ low: z.number(), high: z.number() }).refine((b) => b.high > b.low, "The high end must be above the low end.");
export const UTILISATION_TARGETS = z.strictObject({ low: z.number().min(0).max(100), high: z.number().min(0).max(150), byRole: z.record(z.string(), UTILISATION_BAND) }).refine((t) => t.high > t.low, "The high end must be above the low end.");
export type UtilisationTargets = z.infer<typeof UTILISATION_TARGETS>;
export const DEFAULT_UTILISATION_TARGETS: UtilisationTargets = { low: 70, high: 80, byRole: {} };

/** Inbox open tracking (X6-18a, Settings → Inbox): when `opens` is on, a 1:1 inbox or ticket email carries an open pixel and
 *  the first open sets the message's readAt. Read by mod-mail actions (sendQueuedMessage). */
export const INBOX_TRACKING = z.strictObject({ opens: z.boolean() });
export type InboxTracking = z.infer<typeof INBOX_TRACKING>;
export const DEFAULT_INBOX_TRACKING: InboxTracking = { opens: true };
/** Whether the inbox hours are the ones saved under Settings → Inbox hours (true) or follow the workspace working hours
 *  (false, the default). Set by PUT /inbox/hours and cleared by POST /inbox/hours/reset (mod-inbox sla.ts). */
export const INBOX_HOURS_OWN = z.boolean();

/** When a project's health turns "At risk" or "Off track" (X5-14). A project is at risk when the share of its budget used is
 *  over `atRiskBurnPct` or its margin is under `atRiskMarginPct`; it is off track past the second pair. Retainers have no
 *  budget to burn, and a project that has recognised nothing yet has no margin, so each is judged on what it has. The reason
 *  of the rule that fired is written on the project (mod-delivery projects.health). Edited on the Projects page. */
export const PROJECT_HEALTH = z
  .strictObject({
    atRiskBurnPct: z.number().min(1).max(1000),
    offTrackBurnPct: z.number().min(1).max(1000),
    atRiskMarginPct: z.number().min(-1000).max(100),
    offTrackMarginPct: z.number().min(-1000).max(100),
  })
  .refine((r) => r.offTrackBurnPct > r.atRiskBurnPct, "Off track must start at a higher budget use than at risk.")
  .refine((r) => r.offTrackMarginPct < r.atRiskMarginPct, "Off track must start at a lower margin than at risk.");
export type ProjectHealthRules = z.infer<typeof PROJECT_HEALTH>;
export const DEFAULT_PROJECT_HEALTH: ProjectHealthRules = { atRiskBurnPct: 100, offTrackBurnPct: 120, atRiskMarginPct: 0, offTrackMarginPct: -25 };

/** The delivery targets shown beside the metrics: first-pass QA share and revision rounds (Settings, Work). Read by mod-delivery projects.ts. */
export const DELIVERY_TARGETS = z.strictObject({ qaFirstPassPct: z.number().int().min(1).max(100), maxRevisionRounds: z.number().int().min(1).max(20) });
export type DeliveryTargets = z.infer<typeof DELIVERY_TARGETS>;
export const DEFAULT_DELIVERY_TARGETS: DeliveryTargets = { qaFirstPassPct: 85, maxRevisionRounds: 2 };

/** The delivery rating a client is asked when a project is marked delivered (Settings, Work: deliveryRating): on or off, csat or
 *  nps, and the question (1 to 200 characters). A saved value that does not parse falls back to DEFAULT_DELIVERY_RATING.
 *  Read by mod-delivery projects.ts. */
export const DELIVERY_RATING = z.strictObject({
  enabled: z.boolean(),
  kind: z.enum(["csat", "nps"]),
  question: z.string().trim().min(1).max(200),
});
export type DeliveryRating = z.infer<typeof DELIVERY_RATING>;
export const DEFAULT_DELIVERY_RATING: DeliveryRating = { enabled: true, kind: "csat", question: "How did we do on this project?" };

/** Client health rules (Settings → Clients → Health score, card X5-22a): the points each factor takes off a client's score
 *  of 100 (0 to 100 each) and the two tone thresholds. Good from `goodFrom`, watch from `watchFrom`, risk below. The
 *  defaults are the shipped rules. Read by mod-clients health.ts on every recompute. */
export const HEALTH_RULES = z
  .strictObject({
    /** An overdue invoice. */
    overdue: z.number().int().min(0).max(100),
    /** An invoice unpaid for more than 30 days. */
    oldUnpaid: z.number().int().min(0).max(100),
    /** No contact for 30 days or more. */
    silence30: z.number().int().min(0).max(100),
    /** No contact for 60 days or more (replaces silence30). */
    silence60: z.number().int().min(0).max(100),
    /** The latest NPS answer below 7. */
    npsBelow7: z.number().int().min(0).max(100),
    /** The latest CSAT answer at 2 or below (of 5). */
    csatLow: z.number().int().min(0).max(100),
    /** Each open urgent ticket. */
    urgentEach: z.number().int().min(0).max(100),
    /** The most the urgent tickets can take off. */
    urgentMax: z.number().int().min(0).max(100),
    /** The retainer is over its hours. */
    retainerOver: z.number().int().min(0).max(100),
    /** The client's recent messages read as unhappy (sentiment by rules, card X13-0103a). A saved rules value without this key takes the default. */
    sentimentNegative: z.number().int().min(0).max(100).default(15),
    /** A score this high or higher is good. */
    goodFrom: z.number().int().min(0).max(100),
    /** A score this high or higher (below goodFrom) is watch; lower is risk. */
    watchFrom: z.number().int().min(0).max(100),
  })
  .refine((r) => r.goodFrom > r.watchFrom, { message: "Good must start above watch.", path: ["goodFrom"] });
export type HealthRules = z.infer<typeof HEALTH_RULES>;
export const DEFAULT_HEALTH_RULES: HealthRules = { overdue: 30, oldUnpaid: 15, silence30: 20, silence60: 30, npsBelow7: 20, csatLow: 20, urgentEach: 10, urgentMax: 30, retainerOver: 10, sentimentNegative: 15, goodFrom: 80, watchFrom: 50 };

/** Purchase order approvals (Settings → Money → Purchase approvals, X2-12): each tier lists who approves orders up to
 *  `upTo` (in the workspace currency, null = any amount). An order needs the tier it falls in and every tier below it, one
 *  after the other; any one person of a tier decides that step. With no tiers, anyone but the requester can approve. */
export const PO_TIER = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  label: z.string().trim().min(1).max(40),
  upTo: z.number().positive().nullable(),
  approverIds: z.array(z.string().min(1)).min(1).max(10),
});
export const PO_APPROVAL = z.strictObject({
  tiers: z.array(PO_TIER).max(6)
    .refine((t) => new Set(t.map((x) => x.id)).size === t.length, "Two tiers share an id.")
    .refine((t) => ascending(t.filter((x) => x.upTo !== null).map((x) => x.upTo as number)) && t.every((x, i) => x.upTo !== null || i === t.length - 1), "Limits must go up, with the no-limit tier last."),
});
export type PoApproval = z.infer<typeof PO_APPROVAL>;
export const DEFAULT_PO_APPROVAL: PoApproval = { tiers: [] };

/** Proposal limits (Settings, Money). A proposal past one of them needs approval before it can be sent; the approver is the
 *  "proposal" rule under Approvals. `priceBands` are the ascending upper limits, in major units, that the pricing guidance
 *  groups past proposals by; the prices above the last limit form one more band. */
export const PROPOSAL_RULES = z.strictObject({
  discountOverPct: z.number().min(0).max(100).optional(),
  valueOver: z.strictObject({ amount: z.number().min(0), currency: z.string().length(3) }).optional(),
  marginUnderPct: z.number().min(0).max(100).optional(),
  priceBands: z.array(z.number().min(0)).max(10).refine((b) => ascending(b), "Price bands must go up.").optional(),
  /** Days after sending that an unopened proposal gets its one reminder (default 3). */
  remindAfterDays: z.number().int().min(1).max(60).optional(),
  /** Days a new proposal stays valid (default 30). */
  expiryDays: z.number().int().min(1).max(365).optional(),
});
export type ProposalRules = z.infer<typeof PROPOSAL_RULES>;
export const DEFAULT_PROPOSAL_RULES: ProposalRules = {  };

/** Agreement rules (Settings, Money). With approveClientChanges on, a change a client proposed that is not already in the
 *  clause library waits for an approval of kind "agreement-terms" before the agency can accept it (X7-14g). */
export const AGREEMENT_RULES = z.strictObject({
  approveClientChanges: z.boolean().optional(),
  // X18-E27: an agreement over these limits, or of a listed kind, needs an approval of the "agreement-send" kind before it is sent
  sendApproval: z.strictObject({
    valueOver: z.strictObject({ amount: z.number().nonnegative(), currency: z.string() }).optional(),
    kinds: z.array(z.string()).optional(),
  }).optional(),
});
export type AgreementRules = z.infer<typeof AGREEMENT_RULES>;
export const DEFAULT_AGREEMENT_RULES: AgreementRules = {  };

/** The workspace's working calendar, for capacity and due dates. Dates are YYYY-MM-DD strings, so no time zone applies. */
export function workingHours(setting: WorkingHours = DEFAULT_WORKING_HOURS) {
  const weekday = (ymd: string) => ((new Date(`${ymd}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;
  const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
  /** The holiday on this date (the workspace's own list first, then the national dates of its country), or null. */
  const holidayOn = (ymd: string): string | null => {
    const own = setting.holidays.find((h) => h.date === ymd);
    if (own) return own.label;
    if (setting.country === "none") return null;
    const [, month, day] = ymd.split("-").map(Number);
    return FIXED_HOLIDAYS[setting.country].find(([m, d]) => m === month && d === day)?.[2] ?? null;
  };
  const isWorkingDay = (ymd: string) => setting.days.includes(weekday(ymd)) && holidayOn(ymd) === null;
  return {
    holidayOn,
    isWorkingDay,
    hoursPerDay: (minutes(setting.end) - minutes(setting.start)) / 60,
    /** Working days from `from` to `to`, both included (0 when `to` is earlier). */
    workingDaysBetween: (from: string, to: string): number => {
      let count = 0;
      const last = Date.parse(`${to}T00:00:00Z`);
      for (let t = Date.parse(`${from}T00:00:00Z`); t <= last; t += DAY_MS) if (isWorkingDay(new Date(t).toISOString().slice(0, 10))) count++;
      return count;
    },
    /** Every holiday of the year in date order: the national dates of the country, then the workspace's own. */
    holidaysIn: (year: number): { date: string; label: string }[] => {
      const national = setting.country === "none" ? [] : FIXED_HOLIDAYS[setting.country].map(([m, d, label]) => ({ date: `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`, label }));
      const own = setting.holidays.filter((h) => h.date.startsWith(`${year}-`)).map((h) => ({ date: h.date, label: h.label }));
      return [...national, ...own].sort((a, b) => a.date.localeCompare(b.date));
    },
  };
}

/** Key patterns (Settings → Workspace → Codes): {client} client code, {project} project key, {yyyy} the year, {seq} the
 *  running number padded to `seqDigits`. Tokens with no value stay as typed, so an incomplete key is visible. */
const KEY_TOKEN = /\{(client|project|yyyy|seq)\}/g;
const KEY_PATTERN = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .refine((p) => p.includes("{seq}"), "The pattern needs {seq}.")
  .refine((p) => ![...p.matchAll(/\{(\w*)\}/g)].some((t) => !["client", "project", "yyyy", "seq"].includes(t[1]!)), "Use only {client}, {project}, {yyyy} and {seq}.")
  .refine((p) => !/[{}]/.test(p.replace(KEY_TOKEN, "")), "Braces are only for the four tokens.")
  .refine((p) => /^[A-Za-z0-9{}_.\- ]+$/.test(p), "Use letters, numbers, dashes, dots and underscores.");
export const NAMING = z
  .strictObject({
    clientCodeLength: z.number().int().min(2).max(6),
    projectKey: KEY_PATTERN,
    taskKey: KEY_PATTERN,
    seqDigits: z.number().int().min(1).max(4),
    /** The stored name of a new upload: this pattern plus the original extension (X11-418a). Defaulted, so a naming saved
     *  before this key existed still parses and keeps every other setting. */
    fileName: z.string().min(1).max(120).default("{client}-{seq}"),
    /** The SKU of a new service (X11-418a). Defaulted for the same reason as fileName. */
    serviceSku: z.string().min(1).max(60).default("SVC-{seq}"),
  })
  .refine((n) => n.taskKey.includes("{project}"), { path: ["taskKey"], message: "The task key pattern needs {project}, so keys stay unique across projects." })
  .refine((n) => n.fileName.includes("{seq}"), { path: ["fileName"], message: "Add {seq} to the pattern so each name is unique." })
  .refine((n) => n.serviceSku.includes("{seq}"), { path: ["serviceSku"], message: "Add {seq} to the pattern so each SKU is unique." });
export type Naming = z.infer<typeof NAMING>;
export const DEFAULT_NAMING: Naming = { clientCodeLength: 3, projectKey: "{client}-{seq}", taskKey: "{project}-{seq}", seqDigits: 2, fileName: "{client}-{seq}", serviceSku: "SVC-{seq}" };

/** Fills a key pattern from the values it has. */
export function renderKey(pattern: string, values: { client?: string; project?: string; year?: number; seq: number }, seqDigits = 2): string {
  return pattern
    .replaceAll("{client}", values.client ?? "{client}")
    .replaceAll("{project}", values.project ?? "{project}")
    .replaceAll("{yyyy}", values.year === undefined ? "{yyyy}" : String(values.year))
    .replaceAll("{seq}", String(values.seq).padStart(seqDigits, "0"));
}

/** The daily digest (Settings → Notifications → Digests): whether it goes out and when, in the workspace time zone. */
export const DIGEST = z.strictObject({ enabled: z.boolean(), time: HHMM });
export type Digest = z.infer<typeof DIGEST>;
export const DEFAULT_DIGEST: Digest = { enabled: true, time: "07:00" };

/** Trash lifetimes (Settings → Data → Keep data for): days a trashed item stays before it is removed for good, per
 *  collection that has a trash (today: files). 0 keeps items until someone empties the trash. */
export const RETENTION = z.strictObject({
  trashedDays: z.strictObject({ files: z.number().int().min(0).max(3650) }),
  /** Days the audit trail is kept (X2-05); rows older than this are removed once a day. 0 keeps them for good. */
  auditDays: z.number().int().min(0).max(3650).optional(),
  /** Days a record of each collection is kept before its personal fields are anonymised (X11-722a). The row stays; an
   *  absent collection keeps its rows as they are. */
  recordDays: z
    .strictObject({
      leads: z.number().int().min(30).max(3650).optional(),
      bookings: z.number().int().min(30).max(3650).optional(),
      feedback: z.number().int().min(30).max(3650).optional(),
      audienceMembers: z.number().int().min(30).max(3650).optional(),
      formResponses: z.number().int().min(30).max(3650).optional(),
    })
    .optional(),
});
export type Retention = z.infer<typeof RETENTION>;
export const DEFAULT_RETENTION: Retention = { trashedDays: { files: 30 } };

/** The audit stream (X21-OPS-15): each new audit row is posted to `url` as a JSON body signed with the stream secret. The
 *  secret is not stored here; it is made once with POST /audit/stream/secret and sealed in the vault. Only https passes. */
export const AUDIT_STREAM = z.strictObject({
  enabled: z.boolean(),
  url: z.string().max(500).refine((v) => v === "" || /^https:\/\/[^\s/]+\S*$/i.test(v), "Use a public https address."),
});
export type AuditStream = z.infer<typeof AUDIT_STREAM>;
export const DEFAULT_AUDIT_STREAM: AuditStream = { enabled: false, url: "" };

/** Revision rounds included per scope item when the item names none of its own (X11-709a). A scope item with its own allowance keeps it. */
export const REVISION_ALLOWANCE = z.number().int().min(0).max(20);
export const DEFAULT_REVISION_ALLOWANCE = 2;

/** A data-subject request (Settings → Data → Data requests): logged when it arrives and answered by its due date. */
export const PRIVACY_KIND = z.enum(["access", "export", "delete"]);
export const PRIVACY_REQUEST = z.strictObject({
  id: z.string().regex(/^[a-z0-9-]{8,64}$/),
  kind: PRIVACY_KIND,
  subject: z.string().trim().min(3).max(200),
  note: z.string().trim().max(500).default(""),
  requestedAt: z.iso.datetime(),
  dueAt: z.iso.datetime(),
  status: z.enum(["open", "done"]),
  closedAt: z.iso.datetime().optional(),
});
export type PrivacyRequest = z.infer<typeof PRIVACY_REQUEST>;
export const PRIVACY_REQUESTS = z.array(PRIVACY_REQUEST).max(500);
/** Days from a request arriving to its due date. */
export const PRIVACY_DUE_DAYS = 30;
export const privacyDueAt = (requestedAt: Date): string => new Date(requestedAt.getTime() + PRIVACY_DUE_DAYS * DAY_MS).toISOString();
/** Where a request stands at `now`: done, overdue, due within five days, or open. */
export function privacyState(r: PrivacyRequest, now: Date): "open" | "due-soon" | "overdue" | "done" {
  if (r.status === "done") return "done";
  const left = Date.parse(r.dueAt) - now.getTime();
  return left < 0 ? "overdue" : left < 5 * DAY_MS ? "due-soon" : "open";
}

/** Consent wording (Settings → Data → Consent records): the sentence shown where a person agrees to be contacted. */
export const CONSENT = z.strictObject({ wording: z.string().trim().max(2000) });
export type Consent = z.infer<typeof CONSENT>;
/** Data processing agreement template (Settings → Data → DPA). */
export const DPA = z.strictObject({ template: z.string().trim().max(20000) });
export type Dpa = z.infer<typeof DPA>;
/** Business associate agreement template (Settings → Data → BAA). Recording a BAA consent copies this text (X13-1604a). */
export const BAA = z.strictObject({ template: z.string().trim().max(20000) });
export type Baa = z.infer<typeof BAA>;
/** Where the workspace's records came from (Settings → Data → Move from another tool). */
export const MIGRATION = z.strictObject({
  source: z.enum(["none", "hubspot", "salesforce", "pipedrive", "spreadsheet", "other"]),
  notes: z.string().trim().max(2000),
});
export type Migration = z.infer<typeof MIGRATION>;
/** The client portal's name, welcome, first-run steps and the two client emails (Settings → Portal). */
export const PORTAL = z.strictObject({
  name: z.string().trim().max(80),
  welcome: z.string().trim().max(1000),
  steps: z.array(z.string().trim().min(1).max(120)).max(8),
  inviteEmail: z.string().trim().max(2000),
  reminderEmail: z.string().trim().max(2000),
  /** Hosts a client's embedded app may load from (X4-11, B210): https only, exact host names. Unset: no embeds load. */
  embedHosts: z.array(z.string().trim().min(3).max(253)).max(20).optional(),
  /** The languages the portal offers (X4-11, B216). Unset: English, the only language with text. */
  languages: z.array(z.string().trim().min(2).max(35)).min(1).max(12).optional(),
});
export type Portal = z.infer<typeof PORTAL>;
/** Where the workspace's data is stored (Settings → Workspace → Data location). Shown and exported with the workspace. */
export const RESIDENCY = z.strictObject({ region: z.enum(["au", "us", "eu"]) });
export type Residency = z.infer<typeof RESIDENCY>;
/** Pilot-client go-live steps the person ticked (Settings → Go-live). The computed steps are not stored. */
export const GO_LIVE = z.strictObject({ done: z.array(z.string().regex(/^[a-z][a-z0-9-]{0,39}$/)).max(40) });
export type GoLive = z.infer<typeof GO_LIVE>;

/** The go-live steps the workspace's own rows settle. A step is done when its fact is met (see goLiveDone). */
export const GO_LIVE_STEPS = ["mailbox", "brand", "client", "teammate", "proposal", "agreement", "invoice", "deposit"] as const;
export type GoLiveStep = (typeof GO_LIVE_STEPS)[number];
export type GoLiveFacts = { mailboxes: number; brandSet: boolean; clients: number; people: number; proposalsSent: number; agreementsSigned: number; invoicesSent: number; depositsPaid: number };

/** Counts the facts from the rows the web already loads (the workspace, channel connections, clients, people, proposals,
 *  agreements, invoices). Mail kinds are the connections that send and receive mail. A deposit is a practice invoice
 *  (`practice` true) with a paid amount above zero; nothing is stored. */
export function goLiveFacts(input: {
  workspaceId: string;
  workspaces: { id: string; brand?: { logo?: string; accent?: string } }[];
  channelConnections: { kind: string }[];
  clients: unknown[];
  people: unknown[];
  proposals: { status: string }[];
  agreements: { status: string }[];
  invoices: { status: string; practice?: boolean; paid?: { amount: number } }[];
}): GoLiveFacts {
  const mail = ["gmail", "outlook", "imap", "shared"];
  const brand = input.workspaces.find((w) => w.id === input.workspaceId)?.brand;
  return {
    mailboxes: input.channelConnections.filter((c) => mail.includes(c.kind)).length,
    brandSet: Boolean(brand?.logo || brand?.accent),
    clients: input.clients.length,
    people: input.people.length,
    proposalsSent: input.proposals.filter((p) => ["sent", "opened", "accepted"].includes(p.status)).length,
    agreementsSigned: input.agreements.filter((a) => a.status === "signed").length,
    invoicesSent: input.invoices.filter((i) => ["sent", "viewed", "partial", "paid", "overdue"].includes(i.status)).length,
    depositsPaid: input.invoices.filter((i) => i.practice === true && (i.paid?.amount ?? 0) > 0).length,
  };
}

/** Each computed go-live step, done or not. A teammate means someone besides the owner has a seat. */
export function goLiveDone(f: GoLiveFacts): Record<GoLiveStep, boolean> {
  return {
    mailbox: f.mailboxes > 0,
    brand: f.brandSet,
    client: f.clients > 0,
    teammate: f.people > 1,
    proposal: f.proposalsSent > 0,
    agreement: f.agreementsSigned > 0,
    invoice: f.invoicesSent > 0,
    deposit: f.depositsPaid > 0,
  };
}

type ProjectStatus = z.infer<typeof STATUS>;

/** Project statuses a new project starts with (Settings, Work). Each keeps its group and client label; `emailOnChange`
 *  emails the client when a task moves into it, and `qaGate` makes it the QA gate. A project needs a status in Done. */
export const PROJECT_STATUSES = z.array(STATUS).min(1).max(30).refine(uniqueIds, "Two statuses share an id.").refine((a) => a.some((s) => s.group === "done"), "Keep one status in the Done group.");
export const DEFAULT_PROJECT_STATUSES: ProjectStatus[] = [
  { id: "backlog", label: "Backlog", group: "backlog", clientLabel: "Planned" },
  { id: "todo", label: "To do", group: "todo", clientLabel: "Planned" },
  { id: "progress", label: "In progress", group: "progress", clientLabel: "In progress" },
  { id: "review", label: "In review", group: "review", clientLabel: "Ready for your review" },
  { id: "done", label: "Done", group: "done", clientLabel: "Done" },
  { id: "cancelled", label: "Cancelled", group: "cancelled", clientLabel: "Cancelled" },
];

/** Budget templates (Settings, Work): one per budget type. A new project of that type starts with its amount and hours. */
export const BUDGET_TEMPLATE = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  label: z.string().trim().min(1).max(60),
  budgetType: BUDGET_TYPE,
  amount: z.number().min(0).max(100_000_000),
  currency: z.string().regex(/^[A-Z]{3}$/),
  hours: z.number().min(0).max(100_000),
});
export const BUDGET_TEMPLATES = z.array(BUDGET_TEMPLATE).max(20).refine(uniqueIds, "Two templates share an id.").refine((a) => new Set(a.map((t) => t.budgetType)).size === a.length, "Each budget type has one template.");

/** Queue tiers (Settings, Work): the turnaround a request filed under the tier must meet. The tier's hours replace the item's. */
export const QUEUE_TIER = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  label: z.string().trim().min(1).max(40),
  slaHours: z.number().min(1).max(24 * 60),
  hue: HUE,
});
export const QUEUE_TIERS = z.array(QUEUE_TIER).max(20).refine(uniqueIds, "Two tiers share an id.");

/** Work assignment rules (X9-192a): the assignment rules a role task runs through, in order (the queue is always last). */
export const ASSIGN_RULES = ["named", "round_robin", "least_loaded", "skill", "owner"] as const;
/** Who gets the work: the open-task cap, the rule order, and the days after which overdue or blocked work is escalated. */
export const WORK_RULES = z
  .strictObject({
    maxOpen: z.number().int().min(1).max(200),
    escalateLeadDays: z.number().int().min(1).max(60),
    escalatePmDays: z.number().int().min(1).max(120),
    rules: z.array(z.enum(ASSIGN_RULES)).min(1).max(5),
  })
  .refine((v) => v.escalatePmDays >= v.escalateLeadDays, "The project manager is told on or after the role lead.")
  .refine((v) => new Set(v.rules).size === v.rules.length, "Each rule can be listed once.");
export type WorkRules = z.infer<typeof WORK_RULES>;
/** The values until the agency saves its own: today's behaviour exactly. */
export const DEFAULT_WORK_RULES: WorkRules = { maxOpen: 10, escalateLeadDays: 2, escalatePmDays: 4, rules: ["named", "round_robin", "least_loaded", "skill", "owner"] };

/** Home cards (X6-03b): the widgets a role's Home board can show. The ids are stored; the web maps each to its widget. */
export const HOME_CARDS = ["clientHealth", "waiting", "renewals", "retainer", "myTasks", "reviews", "proofs", "briefs", "drafts", "invoices", "payouts", "unbilled", "deals", "followUps", "proposals", "news"] as const;
/** The seven roles the first Home boards were built for (X9). A board may now be saved for any role on the team list (X11-528a). */
export const HOME_BOARD_ROLES = ["project-manager", "account-manager", "developer", "designer", "copywriter", "finance", "sales"] as const;
/** One role's Home board: its cards in order, and whether people in the role may change it themselves. */
export const HOME_BOARD_SETTING = z.object({
  cards: z.array(z.string().max(40)).max(12)
    .refine((cards) => cards.every((c) => (HOME_CARDS as readonly string[]).includes(c)), "Use the cards Home offers.")
    .refine((cards) => new Set(cards).size === cards.length, "A card can be on a board once."),
  locked: z.boolean(),
});
/** The Home boards by role id (X11-528a): a board for any role on the agency's team list (setting teamRoles). The save route refuses a key that is not on that list. */
export const HOME_BOARDS_SETTING = z.record(z.string().min(1).max(60), HOME_BOARD_SETTING);
export type HomeBoardsSetting = z.infer<typeof HOME_BOARDS_SETTING>;

/** The client view (X11-529a): a team label in place of person names, and days added to an internal due date for the client when no client date is set. */
export const CLIENT_VIEW = z.strictObject({
  hideNames: z.boolean().default(false),
  teamLabel: z.string().trim().min(1).max(60).default("Design team"),
  dateBuffer: z.number().int().min(0).max(30).default(0),
});
export const DEFAULT_CLIENT_VIEW: ClientView = { hideNames: false, teamLabel: "Design team", dateBuffer: 0 };
export type ClientView = z.infer<typeof CLIENT_VIEW>;

/** Workflow publishing: when on, a person who is not an owner or admin publishes a version that waits for approval (X6-12b). */
export const WORKFLOW_PUBLISH_APPROVAL = z.strictObject({ enabled: z.boolean() });
export type WorkflowPublishApproval = z.infer<typeof WORKFLOW_PUBLISH_APPROVAL>;
/** CRM stage mapping (X6-05b): each remote stage (HubSpot stage id, Salesforce StageName) to a local stage id, per CRM. An empty
 *  map matches stages by label. Read by mod-connectors sync/crm.ts. */
export const CRM_STAGE_MAP = z.strictObject({
  hubspot: z.record(z.string(), z.string()).default({}),
  salesforce: z.record(z.string(), z.string()).default({}),
});
export type CrmStageMap = z.infer<typeof CRM_STAGE_MAP>;
/** The remote stages each CRM had at its last sync (X6-05b), so the mapping dialog still lists them offline. Written by sync/crm.ts. */
export const CRM_REMOTE_STAGES = z.strictObject({
  hubspot: z.array(z.strictObject({ id: z.string().max(200), label: z.string().max(200) })).max(200).default([]),
  salesforce: z.array(z.strictObject({ id: z.string().max(200), label: z.string().max(200) })).max(200).default([]),
});
/** Lead capture (X6-05b): the sales inboxes whose new senders become leads, and whether a website chat visitor who left an email
 *  becomes one. `channels` (X13-0107a) lists the phone channels whose new senders become leads, keyed by their phone number.
 *  Read by mod-growth leads.ts. A lead made this way has no consent basis until one is recorded. */
export const LEAD_CAPTURE_PHONE_CHANNELS = ["whatsapp", "sms", "telegram"] as const;
export const LEAD_CAPTURE = z.strictObject({
  emailInboxes: z.array(z.string().max(120)).max(20).default([]),
  chat: z.boolean().default(false),
  channels: z.array(z.enum(LEAD_CAPTURE_PHONE_CHANNELS)).max(3).default([]),
});
export type LeadCapture = z.infer<typeof LEAD_CAPTURE>;

/** Campaign recipient sunset (X11-806a): a person with 3 or more sends and no human open, click or reply in this many days is
 *  left out of the next send. null switches the rule off. Read by mod-mail engagement.ts. */
export const SUNSET_DAYS = z.number().int().min(30).max(730).nullable();

/** Sunset notice (X21-MKT-16): the email a person gets once, before the sunset rule leaves them out. The stay link is added after
 *  the body. Off by default: the sweep mails real people from the agency mailbox, so an agency turns it on. Read by mod-mail sunset-flow.ts. */
export const SUNSET_NOTICE_DEFAULT: { enabled: boolean; subject: string; body: string } = {
  enabled: false,
  subject: "Do you still want to hear from us?",
  body: "We have not heard from you in a while, so we will stop sending you email. If you still want our emails, use the link below to stay on the list.\n\nIf we hear nothing from you, you will be taken off the list in 14 days.",
};
export const SUNSET_EMAIL = z.object({
  enabled: z.boolean().default(SUNSET_NOTICE_DEFAULT.enabled),
  subject: z.string().trim().min(1).max(200).default(SUNSET_NOTICE_DEFAULT.subject),
  body: z.string().trim().min(1).max(4000).default(SUNSET_NOTICE_DEFAULT.body),
});
export type SunsetEmail = z.infer<typeof SUNSET_EMAIL>;

/** The agency's own list of fields a client never sees, per collection (X6-03a), read by authz policy.ts clientHiddenOf. A
 *  collection present here replaces its built-in INTERNAL_FIELDS list; a collection absent keeps the built-in list. */
export const CLIENT_HIDDEN_FIELDS = z.record(z.string().max(60), z.array(z.string().max(60)).max(60));

/** One class of the agency's managed asset class list (X11-530a, Settings, Files). Its id is a slug that never changes, so the files
 *  that carry it keep pointing at it; an archived class stays on those files but takes no new ones. */
export const ASSET_CLASS_ENTRY = z.strictObject({ id: ASSET_CLASS_ID, label: z.string().trim().min(1).max(80), archived: z.boolean().default(false) });
export type AssetClassEntry = z.infer<typeof ASSET_CLASS_ENTRY>;
/** The managed list: at most 60 classes, each id once, and at least one open. LIST_DEFAULTS holds the built-in five. */
export const ASSET_CLASSES = z.array(ASSET_CLASS_ENTRY).max(60).refine(uniqueIds, "Two classes share an id.").refine((a) => a.some((x) => !x.archived), "Keep at least one class open.");
export const DEFAULT_ASSET_CLASSES: AssetClassEntry[] = [
  { id: "deliverable", label: "Deliverables", archived: false },
  { id: "source", label: "Source", archived: false },
  { id: "brand", label: "Brand", archived: false },
  { id: "contract", label: "Contracts", archived: false },
  { id: "internal", label: "Internal", archived: false },
];

/** Per role id: the asset classes closed to that role (X7-04g). A role absent or with [] opens every class. The Owner role is
 *  never restricted. Read by authz policy.ts assetClassAccessOf. */
export const ASSET_CLASS_ACCESS = z.record(z.string().min(1).max(200), z.array(ASSET_CLASS_ID).max(60));
export type AssetClassAccess = z.infer<typeof ASSET_CLASS_ACCESS>;

/** The order of the catalogue's service folders (X7-01c). A service's folder is a plain text field, so this list only sets
 *  the order the catalogue shows them in; folders not listed follow alphabetically. Each folder can be listed once. */
export const SERVICE_FOLDERS = z.array(z.string().trim().min(1).max(60)).max(100)
  .refine((a) => new Set(a.map((x) => x.toLowerCase())).size === a.length, "Each folder can be listed once.");
/** The rate card levels the agency defines (X7-01d): 1 to 12 names, 1 to 40 characters each, no name twice (case-insensitive). */
export const RATE_LEVELS = z.array(z.string().trim().min(1).max(40)).min(1).max(12)
  .refine((a) => new Set(a.map((x) => x.toLowerCase())).size === a.length, "Each level can be listed once.");
export const DEFAULT_RATE_LEVELS: string[] = ["junior", "mid", "senior", "lead"];
/** Access that ends by itself (X7-04k): whether a freelancer whose every project is closed gets an end date, and how many days after. */
export const ACCESS_END = z.strictObject({ freelancersOnProjectClose: z.boolean(), graceDays: z.number().int().min(0).max(90) });
export type AccessEnd = z.infer<typeof ACCESS_END>;
export const DEFAULT_ACCESS_END: AccessEnd = { freelancersOnProjectClose: false, graceDays: 7 };
/** Year-end tax forms (X13-2209c): `nec1099Threshold` is the total in whole units of the workspace currency at which a US
 *  1099 contractor gets a 1099-NEC row. Default 2000: the threshold for payments made after 2025 (One Big Beautiful Bill Act,
 *  Public Law 119-21; the figure was checked on 2026-10-26 against a web search, the IRS instructions page was not readable,
 *  so re-check the IRS instructions for the year before filing). Read by mod-people tax-forms.ts. */
export const TAX_FORMS = z.strictObject({ nec1099Threshold: z.number().min(0).max(1_000_000) });
export type TaxFormsSetting = z.infer<typeof TAX_FORMS>;
export const DEFAULT_TAX_FORMS: TaxFormsSetting = { nec1099Threshold: 2000 };

/** Site-wide SEO defaults (X9-68a): a published page with no SEO title or description of its own takes these, and the share image goes on every page. */
export const SITE_SEO = z.strictObject({
  /** Title for a page with no SEO title of its own. {page} is the page's title and {site} the workspace name. Empty: the old rule (the home page is the site name, others "<page> | <site>"). */
  titlePattern: z.string().max(120).default(""),
  /** Description for a page with no description of its own. */
  description: z.string().max(300).default(""),
  /** Share image address (https), used in og:image and twitter:image. Empty: none. */
  image: z.string().max(500).refine((v) => v === "" || /^https:\/\/\S+$/.test(v), "Use an https address.").default(""),
  /** X21-RESA-12: "noindex" asks search engines to leave the whole site out (a noindex tag on every page, Disallow: / in robots.txt). */
  robots: z.enum(["index", "noindex"]).default("index"),
  /** X21-RESA-12: the https address the site is known by (e.g. https://www.example.com). A page with no canonical of its own gets this plus its path. Empty: none. */
  canonicalBase: z.string().max(200).refine((v) => v === "" || /^https:\/\/[^\s/]+(\/\S*)?$/.test(v), "Use an https address.").default(""),
  /** X21-RESA-12: the site's X (Twitter) account for twitter:site. Typed with or without the @; stored without it. Empty: none. */
  twitterHandle: z
    .string()
    .max(16)
    .refine((v) => v === "" || /^@?[A-Za-z0-9_]{1,15}$/.test(v), "Use 1 to 15 letters, digits or underscores.")
    .transform((v) => v.replace(/^@/, ""))
    .default(""),
});
export type SiteSeo = z.infer<typeof SITE_SEO>;

/** The site quality checks (X13-0902a): which of the ten checks run on a site's pages, and the title and description length
 *  limits. A check switched off leaves the list and the score. Read by mod-clients sites.ts siteChecksOf. */
export const SITE_CHECKS = z.strictObject({
  enabled: z.strictObject({
    https: z.boolean().default(true),
    title: z.boolean().default(true),
    description: z.boolean().default(true),
    h1: z.boolean().default(true),
    lang: z.boolean().default(true),
    viewport: z.boolean().default(true),
    canonical: z.boolean().default(true),
    alt: z.boolean().default(true),
    weight: z.boolean().default(true),
    speed: z.boolean().default(true),
  }).prefault({}),
  titleMin: z.number().int().min(0).max(200).default(10),
  titleMax: z.number().int().min(1).max(300).default(65),
  descriptionMin: z.number().int().min(0).max(400).default(50),
  descriptionMax: z.number().int().min(1).max(600).default(160),
  /** The Website page's SEO list limits (X14-0801a): the title and description length ranges and the minimum word count. */
  seoTitleMin: z.number().int().min(0).max(200).default(30),
  seoTitleMax: z.number().int().min(1).max(300).default(60),
  seoDescriptionMin: z.number().int().min(0).max(400).default(70),
  seoDescriptionMax: z.number().int().min(1).max(600).default(160),
  seoMinWords: z.number().int().min(0).max(5000).default(150),
}).refine((s) => s.titleMin <= s.titleMax, "The shortest title cannot be longer than the longest.")
  .refine((s) => s.descriptionMin <= s.descriptionMax, "The shortest description cannot be longer than the longest.")
  .refine((s) => s.seoTitleMin <= s.seoTitleMax, "The shortest SEO title cannot be longer than the longest.")
  .refine((s) => s.seoDescriptionMin <= s.seoDescriptionMax, "The shortest SEO description cannot be longer than the longest.");
export type SiteChecks = z.infer<typeof SITE_CHECKS>;
export const DEFAULT_SITE_CHECKS: SiteChecks = SITE_CHECKS.parse({});

/** The tools this agency replaces with this platform (X11-801a): each tool's monthly cost and this platform's monthly cost, all in
 *  integer minor units of one currency. Every tool must use the platform's currency, and no two tools share an id. */
export const MIGRATION_STACK = z.strictObject({
  tools: z.array(z.strictObject({
    id: z.string().min(1).max(64),
    name: z.string().trim().min(1).max(80),
    monthlyCost: z.number().int().min(0).max(100000000),
    currency: z.string().length(3),
  })).max(50),
  platformMonthlyCost: z.number().int().min(0).max(100000000),
  platformCurrency: z.string().length(3),
}).superRefine((v, ctx) => {
  const ids = new Set(v.tools.map((t) => t.id));
  if (ids.size !== v.tools.length) ctx.addIssue({ code: "custom", path: ["tools"], message: "Two tools share an id." });
  if (v.tools.some((t) => t.currency !== v.platformCurrency)) ctx.addIssue({ code: "custom", path: ["tools"], message: "Every tool must use the same currency as this platform." });
});
export type MigrationStack = z.infer<typeof MIGRATION_STACK>;

/** The same shape as the DOMAIN pattern in mod-growth verify.ts: a lowercase dotted domain, never an address. */
export const DOMAIN_NAME = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** The workspace's disposable-domain list (X13-1806a): domains added to the built-in starter list and domains removed from it.
 *  The starter list stays in code; this list only adds and removes. Read by mod-growth verify.ts. */
export const DISPOSABLE_DOMAINS = z.strictObject({
  add: z.array(z.string().trim().toLowerCase().max(253)).max(2000).default([]),
  remove: z.array(z.string().trim().toLowerCase().max(253)).max(2000).default([]),
}).superRefine((v, ctx) => {
  for (const path of ["add", "remove"] as const) {
    for (const domain of v[path]) {
      if (!DOMAIN_NAME.test(domain)) ctx.addIssue({ code: "custom", path: [path], message: `${domain} is not a domain.` });
    }
  }
}).transform((v) => ({ add: [...new Set(v.add)], remove: [...new Set(v.remove)] }));
export type DisposableDomains = z.infer<typeof DISPOSABLE_DOMAINS>;

/** The seed-inbox addresses a placement test sends to (X13-2002a): one email per entry, at most 50. Read by mod-mail reputation.ts. */
export const PLACEMENT_SEEDS = z.array(z.string().trim().max(254).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter one email address per line.")).max(50);
/** The seed-test provider a placement test runs on (X13-2002a). Only glockapps is known. Read by mod-mail reputation.ts. */
export const PLACEMENT_PROVIDER = z.enum(["glockapps"]);

/** Lead sourcing (X13-1606a): who confirmed the provider terms, and the secret the Apollo phone webhook must carry. Only the
 *  owner's confirmation route writes acknowledgedBy and acknowledgedAt. */
export const LEAD_SOURCING = z.strictObject({
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  webhookSecret: z.string().nullable(),
});
export type LeadSourcing = z.infer<typeof LEAD_SOURCING>;

/** Workspace-level settings an owner or admin edits. */
/** The permission level (X13-2202a): the defaults a role preset's grants take when it is installed. Balanced is the default. */
export const PERMISSION_LEVEL = z.strictObject({ id: z.enum(["strict", "balanced", "open"]), label: z.string().trim().min(1).max(40) });
export type PermissionLevel = z.infer<typeof PERMISSION_LEVEL>;

/** SMS double opt-in (X21-MKT-11): ISO-2 countries whose numbers must confirm a text before a campaign texts them. Empty: single opt-in. */
export const SMS_DOUBLE_OPT_IN = z.strictObject({ doubleOptIn: z.array(z.string().regex(/^[A-Z]{2}$/)).max(250) });
export type SmsDoubleOptIn = z.infer<typeof SMS_DOUBLE_OPT_IN>;
/** Platform switches (X21-RESA-14, Settings → Access → Platform): maintenance mode for visitors and the client portal
 *  (staff keep working, health checks stay open), and public sign-ups (a new address may create an account). */
export const PLATFORM_SWITCHES = z.strictObject({
  maintenance: z.strictObject({ on: z.boolean(), message: z.string().trim().max(300) }),
  publicSignUps: z.boolean(),
});
export type PlatformSwitches = z.infer<typeof PLATFORM_SWITCHES>;
export const DEFAULT_PLATFORM_SWITCHES: PlatformSwitches = {
  maintenance: { on: false, message: "We are doing scheduled maintenance. We will be back soon." },
  publicSignUps: false,
};

/** The workspace's industry (X21-RESB-13, Settings, General): one of the onboarding industry options, or null when not chosen. */
export const INDUSTRY = z.string().trim().min(1).max(60).nullable();

export const WORKSPACE_SETTINGS = {
  industry: INDUSTRY,
  /** The default rail for everyone in the workspace; a module in `hidden` is off for the whole workspace. */
  modules: MODULES_SETTING,
  /** SMS double opt-in per country (X21-MKT-11). */
  smsDoubleOptIn: SMS_DOUBLE_OPT_IN,
  dunning: DUNNING_SETTING,
  /** Maintenance mode and public sign-ups (X21-RESA-14); read by maintenance-x21.ts and auth.ts. */
  platformSwitches: PLATFORM_SWITCHES,
  /** Lead time of the milestone and task due-date reminders (X21-RESA-04). */
  reminderLead: REMINDER_LEAD,
  revenueLoop: REVENUE_LOOP,
  nudges: NUDGES_SETTING,
  /** The permission level role presets install with (X13-2202a); unset means balanced. */
  permissionLevel: PERMISSION_LEVEL,
  /** Lead sourcing terms and the phone webhook secret (X13-1606a). */
  leadSourcing: LEAD_SOURCING,
  /** Order rules: auto-approve a paid order, lock a delivered one (X21-RESB-03). */
  orderRules: ORDER_RULES,
  /** Agreement reminder cadence, cap per signer and expiry alert days (X6-13b); read by mod-money runAgreements. */
  agreementReminders: AGREEMENT_REMINDERS,
  /** The agency's follow-up sequences (X7-08e); a deal joins one from its Follow-ups action, and its steps are copied at that time. */
  followUpSequences: FOLLOW_UP_SEQUENCES,
  clientStatuses: CLIENT_STATUSES,
  /** The agency's agreement types (X10-62a); each entry's kind is the built-in meaning the agreement rules read. */
  agreementTypes: AGREEMENT_TYPES,
  taskTypes: TASK_TYPES,
  /** The departments a doc belongs to (X9-168a). */
  docDepartments: PLAIN_LIST,
  /** The agency's hiring stages (X9-169a); each entry's kind is the built-in meaning the hiring rules read. */
  candidateStages: CANDIDATE_STAGES,
  leadSources: PLAIN_LIST,
  /** The agency's contact roles at a company (X19-C08); unset means the starting list in clients.ts. */
  contactRoles: PLAIN_LIST,
  lossReasons: PLAIN_LIST,
  /** The job roles the agency staffs by (X9-12a): people hold them, placeholders and demand name them. */
  teamRoles: PLAIN_LIST,
  /** The designations people can hold (X21-RESB-07). Empty until the agency adds its own. */
  designations: DESIGNATIONS,
  /** Dynamic groups (X21-OPS-13b): rule-defined groups whose members are computed on read (GET /api/v1/groups/dynamic/:id/members). */
  dynamicGroups: DYNAMIC_GROUPS,
  /** The agency's ticket priorities (X11-F1-01): a ticket's priority is one of these ids, and the ticket list sorts by rank. */
  ticketPriorities: TICKET_PRIORITIES,
  /** The agency's ticket statuses (X11-306a); each entry's kind is the built-in meaning the SLA clock, the merge and the bulk solve read. */
  ticketStatuses: TICKET_STATUSES,
  /** The product statuses of the public catalogue (X21-RESA-13); unset means the six built-in statuses (web-catalogue-x21.ts). */
  productStatuses: PRODUCT_STATUSES,
  customFields: CUSTOM_FIELDS,
  timeRules: TIME_RULES,
  numbering: NUMBERING,
  paymentTerms: PAYMENT_TERMS,
  /** The optional sending relay (X13-0612a): read by mod-mail relays sendMail; the key lives in the vault, not here. */
  sendingRelay: SENDING_RELAY,
  /** Days the agency aims to take from acceptance to payment (X6-16a); read by mod-money time to cash. */
  timeToCashTarget: TIME_TO_CASH_TARGET,
  /** Bank-transfer details the pay page shows (X5-11); read by mod-money bankOptionsOf. */
  bankTransfer: BANK_TRANSFER,
  /** The ordered payment methods a pay page offers (X13-3501a); read by mod-money payment-methods.ts. */
  paymentMethods: PAYMENT_METHODS,
  payerFees: PAYER_FEES,
  /** The invoice PDF's template, logo, bank details and footer note (X5-04a); read by mod-mail invoiceDocument. */
  invoiceDocument: INVOICE_DOCUMENT,
  company: COMPANY,
  locale: LOCALE,
  displayCurrency: DISPLAY_CURRENCY,
  workingHours: WORKING_HOURS,
  inboxTracking: INBOX_TRACKING,
  inboxHoursOwn: INBOX_HOURS_OWN,
  naming: NAMING,
  digest: DIGEST,
  retention: RETENTION,
  auditStream: AUDIT_STREAM,
  /** Revision rounds included per scope item without its own allowance (X11-709a); read by mod-clients scope baseline. */
  revisionAllowance: REVISION_ALLOWANCE,
  privacyRequests: PRIVACY_REQUESTS,
  consent: CONSENT,
  dpa: DPA,
  baa: BAA,
  migration: MIGRATION,
  portal: PORTAL,
  residency: RESIDENCY,
  goLive: GO_LIVE,
  costModel: COST_MODEL,
  /** Revenue recognition method per budget type (X6-16b, Settings → Money). Read by finance_recognized (R-55-finance.sql). */
  recognition: RECOGNITION,
  /** The month-end close checklist template (X11-621a). Read by mod-money recognition.ts when a finance record is first created. */
  closeTemplate: CLOSE_TEMPLATE,
  /** Markup on client-facing ad spend, percent (X5-09). Read by mod-connectors adSpendMarkupPct; never applied to staff views. */
  adSpendMarkup: AD_SPEND_MARKUP,
  /** The tax rate in basis points that an owner's region apply writes (X13-1208b). null: none set. */
  taxRateBasisPoints: TAX_RATE_BASIS_POINTS,
  /** Whether deposit and instalment invoices carry tax (X13-3202a). */
  chargeTaxOnDeposits: CHARGE_TAX_ON_DEPOSITS,
  /** The legal clauses a region's defaults add (X13-1208b). */
  legalClauses: LEGAL_CLAUSES,
  /** Two-way sync with Xero (X5-15). Read by mod-connectors sync/accounting.ts. */
  accountingSync: ACCOUNTING_SYNC,
  /** Fields a client never sees, per collection (X6-03a). Read by authz policy.ts; default {} = the built-in lists. */
  clientHiddenFields: CLIENT_HIDDEN_FIELDS,
  /** The client portal access matrix (P1b-PORTAL2): per client role and per client, which sections and actions are on. Owners edit it; the server enforces it. */
  clientPortalAccess: CLIENT_PORTAL_ACCESS,
  /** Asset classes closed to a role (X7-04g). Read by authz policy.ts; default {} = nothing closed. */
  assetClassAccess: ASSET_CLASS_ACCESS,
  /** The agency's managed asset class list (X11-530a): the classes files, folders and custom fields carry. Read by mod-files resumable.ts. */
  assetClasses: ASSET_CLASSES,
  budgetAlerts: BUDGET_ALERTS,
  /** The weekly capacity digest to owners, admins and PMs (X6-14b). Read by mod-people digest.ts. */
  capacityDigest: CAPACITY_DIGEST,
  /** The hiring or freelancer suggestion for a job role over the agency's utilisation limit (X9-12b); read by mod-people utilisation.ts. */
  hiring: HIRING_RULES,
  /** The utilisation band per role (X11-623a): the default band is 70 to 80 percent, and a role band wins over it. Read by mod-people capacity.ts. */
  utilisationTargets: UTILISATION_TARGETS,
  /** Client health points and tone thresholds (Settings → Clients → Health score); read by mod-clients health.ts. */
  healthRules: HEALTH_RULES,
  /** The budget-use and margin limits behind a project's At risk and Off track health (X5-14). */
  projectHealth: PROJECT_HEALTH,
  /** The delivery targets shown beside the metrics: first-pass QA share and revision rounds (Settings, Work). Read by mod-delivery projects.ts. */
  deliveryTargets: DELIVERY_TARGETS,
  /** The delivery rating asked when a project is marked delivered: on or off, csat or nps, and its question (Settings, Work). Read by mod-delivery projects.ts. */
  deliveryRating: DELIVERY_RATING,
  poApproval: PO_APPROVAL,
  /** Quiet hours for automations (automation.ts QUIET_HOURS): outward steps wait until the window ends. */
  automationQuietHours: QUIET_HOURS,
  /** Owners and admins approve workflow versions that others publish (X6-12b); read by mod-automation actions.ts. */
  workflowPublishApproval: WORKFLOW_PUBLISH_APPROVAL,
  projectStatuses: PROJECT_STATUSES,
  budgetTemplates: BUDGET_TEMPLATES,
  /** How long a client has to finish onboarding intake (Settings, Onboarding). Read by mod-delivery onboarding.ts and staff-onboarding.ts. */
  onboardingTargets: ONBOARDING_TARGETS,
  /** The internal project for staff run tasks (X13-1409a); read and written by mod-delivery staff-onboarding.ts. */
  internalProjectId: INTERNAL_PROJECT_ID,
  /** Estimated minutes per first-run checklist step (X13-0204a); read by mod-delivery onboarding-first-run.ts. */
  firstRunMinutes: FIRST_RUN_MINUTES,
  queueTiers: QUEUE_TIERS,
  /** Who gets the work (X9-192a): the open-task cap, the assignment rule order, the escalation days. Read by mod-people work-rules.ts. */
  workRules: WORK_RULES,
  /** What the desktop app captures for hourly work (C378); read by mod-time activity.ts and the app on each heartbeat. */
  desktopActivity: DESKTOP_ACTIVITY,
  /** Home boards per role: the cards each role's Home shows and whether the role may change them (X6-03b). */
  homeBoards: HOME_BOARDS_SETTING,
  /** The client view (X11-529a): a team label in place of person names, and a date buffer for the client's due dates. Read by mod-delivery projects.ts (the client preview). */
  clientView: CLIENT_VIEW,
  /** Sales: assignment rules, speed to lead, quotas and activity goals (PHASED-PLAN P5 S3 S4 S6). */
  sales: SALES_SETTING,
  /** CRM stage mapping per CRM (X6-05b); edited in the integrations card's Stage mapping dialog. Read by mod-connectors sync/crm.ts. */
  crmStageMap: CRM_STAGE_MAP,
  /** The remote stages each CRM had at its last sync (X6-05b). Written by sync/crm.ts, read by the stage mapping dialog. */
  crmRemoteStages: CRM_REMOTE_STAGES,
  /** Leads from sales inboxes and website chat (X6-05b); read by mod-growth leads.ts. */
  leadCapture: LEAD_CAPTURE,
  /** Campaign recipient sunset in days, or null for off (X11-806a); read by mod-mail engagement.ts. */
  sunsetDays: SUNSET_DAYS,
  /** The sunset notice email and its subject and body (X21-MKT-16); read by mod-mail sunset-flow.ts. */
  sunsetEmail: SUNSET_EMAIL,
  /** Proposal limits that need approval before sending, and the price bands of the pricing guidance (mod-money proposal-rules.ts). */
  proposalRules: PROPOSAL_RULES,
  /** Agreement rules: approval before a client's change outside the clause library is accepted (X7-14g); read by mod-money agreement-terms.ts. */
  agreementRules: AGREEMENT_RULES,
  /** The AI data policy: off, rules only, or the connected provider (contracts/ai.ts). */
  aiPolicy: AI_POLICY,
  /** The AI reply agent (X21-OPS-24): which reply categories are drafted, which may auto-send, and the confidence floor. Read by mod-ai reply-agent.ts. */
  aiReplyAgent: AI_REPLY_AGENT,
  /** Network allowlist, two-factor roles and session length (X2-05). Read by the server on every request. */
  security: SECURITY,
  /** Single sign-on through OpenID Connect (X2-05). The client secret is sealed on the server, never stored here. */
  sso: SSO,
  /** The order of the catalogue's service folders (X7-01c); read and kept in step by mod-money folders.ts on a rename. */
  serviceFolders: SERVICE_FOLDERS,
  /** The rate card levels the agency defines (X7-01d); the rate card and team roles pick from this list. */
  rateLevels: RATE_LEVELS,
  /** Access that ends by itself (X7-04k). Read by mod-people access-end.ts. */
  accessEnd: ACCESS_END,
  /** Year-end tax forms threshold (X13-2209c). Read by mod-people tax-forms.ts. */
  taxForms: TAX_FORMS,
  /** Site-wide SEO defaults: title pattern, description and share image (X9-68a). Read by mod-clients sites.ts siteSeoOf. */
  siteSeo: SITE_SEO,
  /** Site quality checks on/off and the title and description limits (X13-0902a). Read by mod-clients sites.ts siteChecksOf. */
  siteChecks: SITE_CHECKS,
  /** Owner switch for the first-visit tour prompts; tours stay available from the help menu either way (P8). */
  toursEnabled: TOURS_ENABLED,
  /** The agency's credential kinds (X11-537a): what a credential is. Read by mod-vault and the list refs (packages/api list-refs.ts). */
  credentialKinds: CREDENTIAL_KINDS,
  /** The tools this agency replaces and their monthly cost (X11-801a). Read by mod-connectors migration.ts. */
  migrationStack: MIGRATION_STACK,
  /** The workspace's disposable-domain list (X13-1806a). Read by mod-growth verify.ts. */
  disposableDomains: DISPOSABLE_DOMAINS,
  /** The seed addresses the placement test sends to (X13-2002a); read by mod-mail reputation.ts and written by the reputation panel. */
  placementSeeds: PLACEMENT_SEEDS,
  /** The seed-test provider the placement test runs on (X13-2002a); defaults to glockapps. Read by mod-mail reputation.ts. */
  placementProvider: PLACEMENT_PROVIDER,
} as const;

/** The defaults a list setting falls back to until the agency saves its own. */
export const LIST_DEFAULTS = { clientStatuses: DEFAULT_CLIENT_STATUSES, taskTypes: DEFAULT_TASK_TYPES, leadSources: DEFAULT_LEAD_SOURCES, contactRoles: DEFAULT_CONTACT_ROLES, lossReasons: DEFAULT_LOSS_REASONS, teamRoles: DEFAULT_TEAM_ROLES, designations: DEFAULT_DESIGNATIONS, docDepartments: DEFAULT_DOC_DEPARTMENTS, candidateStages: DEFAULT_CANDIDATE_STAGES, agreementTypes: DEFAULT_AGREEMENT_TYPES, ticketStatuses: DEFAULT_TICKET_STATUSES, ticketPriorities: DEFAULT_TICKET_PRIORITIES, assetClasses: DEFAULT_ASSET_CLASSES, credentialKinds: DEFAULT_CREDENTIAL_KINDS } as const;
export type WorkspaceSettingKey = keyof typeof WORKSPACE_SETTINGS;

/** A pinned record (Search favourites): what the palette and /search need to show and open it. `href` is a path inside the app. */
export const FAVOURITE = z.strictObject({
  kind: z.enum(["client", "project", "task", "invoice", "person", "contact", "deal", "doc", "view"]),
  id: z.string().min(1).max(80),
  title: z.string().min(1).max(200),
  href: z.string().min(1).max(300).refine(
    (h) => h.startsWith("/") && !h.startsWith("//") && !h.includes("\\") && ![...h].some((ch) => ch.charCodeAt(0) < 32),
    "Use a path inside the app, such as /clients/harbour-bakery/overview.",
  ),
  meta: z.string().max(200).optional(),
});
export const FAVOURITES = z.array(FAVOURITE).max(40);

/** Per-person preferences (each person edits only their own). */
export const PERSON_PREFS = {
  /** The person's own rail order; overrides the workspace default but cannot show a module the workspace turned off. */
  rail: PERSON_RAIL_LAYOUT,
  /** The signature added after a line of two dashes when a reply goes out with the signature on (plain text, 1000 characters at most). */
  signature: z.string().max(1000),
  /** The records the person pinned, newest first (at most 40). */
  favourites: FAVOURITES,
  /** Each person's walkthrough progress, synced across devices (P8). */
  tours: TOUR_PROGRESS,
  /** The interface language the person picked, as a language tag such as "en-AU" or "ar". */
  language: z.string().min(2).max(12),
  /** The colour theme the person picked; "system" follows the device. */
  theme: z.enum(["light", "dark", "system"]),
  /** The inbox list's "Group by company" choice (X21-OPS-02b): true groups the open conversations under their company. */
  inboxGroupByCompany: z.boolean(),
} as const;
export type PersonPrefKey = keyof typeof PERSON_PREFS;

/** Off the rail until someone pins them (rail budget ≤10 controls, docs/plan/DONE.md §4); always on the Everything page. */
export const DEFAULT_RAIL_HIDDEN: ModuleId[] = ["people", "reports"];

/** The rail a person sees: their order (else the workspace order, else the shipped order), minus anything hidden. */
export function effectiveRail(workspace?: RailLayout | null, person?: RailLayout | null): ModuleId[] {
  const base = workspace?.order.length ? workspace.order : [...MODULE_IDS];
  const mine = person?.order.length ? person.order : base;
  // modules missing from a saved order (added in a later release) join at the end, in shipped order
  const order = [...new Set([...mine, ...base, ...MODULE_IDS])];
  const off = new Set([...(workspace ? workspace.hidden : DEFAULT_RAIL_HIDDEN), ...(person?.hidden ?? [])]);
  // a module the person pinned on purpose shows even when it is only hidden by default
  const pinned = new Set(person?.order ?? []);
  return order.filter((m) => !off.has(m) || (!workspace && pinned.has(m) && !(person?.hidden ?? []).includes(m)));
}
